import { useEffect, useState, useRef, useMemo } from "react";
import { Link, useSearchParams, useNavigate } from "react-router-dom";
import api from "@/lib/api";
import { usePlzAutocomplete, PlzAutocompleteHint } from "@/lib/usePlzAutocomplete";
import { TENANCY_STATUS, TENANCY_TYPES, RENT_TYPES, DEPOSIT_STATUS_LABELS, INDEX_BASES, SALUTATIONS, formatEUR, formatDate, contactDisplayName, displayEmail, firstEmail, firstPhone, emailListToText } from "@/lib/constants";
import { UNIT_KIND_TO_TENANCY_TYPE } from "@/lib/domivus";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter, DialogTrigger,
} from "@/components/ui/dialog";
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent,
  AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import { Plus, ArrowRight, UserPlus, Info, MagnifyingGlass, EnvelopeSimple, Phone, MapPin, PencilSimple, Trash, Check } from "@phosphor-icons/react";
import { toast } from "sonner";
import { useTableSort } from "@/lib/useTableSort";
import { useAuth } from "@/contexts/AuthContext";
import SearchableCombobox from "@/components/SearchableCombobox";
import { UnitLinkBadges } from "@/components/units/UnitLinks";

const SURCHARGE_PRESETS = ["Möblierungszuschlag", "Untervermietungszuschlag", "Gartennutzung", "Gewerbezuschlag"];

// Zuschläge laut Mietvertrag – erscheinen u. a. einzeln in der Kostentabelle der Mieterhöhung
function SurchargesEditor({ value, onChange }) {
  const upd = (i, k, v) => onChange(value.map((z, j) => (j === i ? { ...z, [k]: v } : z)));
  return (
    <div className="mt-3" data-testid="tenancy-surcharges">
      <div className="flex items-center justify-between">
        <Label className="text-xs text-slate-600">Zuschläge (z. B. Möblierung, Untervermietung)</Label>
        <button type="button" className="text-xs text-teal-700 hover:underline" onClick={() => onChange([...value, { label: "", amount: "" }])} data-testid="tenancy-surcharge-add">+ Zuschlag</button>
      </div>
      {value.map((z, i) => (
        <div key={i} className="flex gap-2 mt-1.5">
          <Input list="surcharge-presets" placeholder="Bezeichnung" value={z.label} onChange={(e) => upd(i, "label", e.target.value)} data-testid={`tenancy-surcharge-label-${i}`} />
          <Input type="number" min="0" step="0.01" placeholder="€ / Monat" className="w-32" value={z.amount} onChange={(e) => upd(i, "amount", e.target.value)} data-testid={`tenancy-surcharge-amount-${i}`} />
          <button type="button" className="text-xs text-rose-700 px-1" onClick={() => onChange(value.filter((_, j) => j !== i))} data-testid={`tenancy-surcharge-remove-${i}`}>Entfernen</button>
        </div>
      ))}
      <datalist id="surcharge-presets">{SURCHARGE_PRESETS.map((p) => <option key={p} value={p} />)}</datalist>
    </div>
  );
}

export default function Tenancies() {
  const { user } = useAuth();
  const isAdmin = ["super_admin", "admin"].includes(user?.role);
  const [items, setItems] = useState([]);
  const [editing, setEditing] = useState(null);
  const [prefill, setPrefill] = useState(null);
  const [properties, setProperties] = useState([]);
  const [contacts, setContacts] = useState([]);
  const [statusFilter, setStatusFilter] = useState("active_only");
  // iter162 — Multi-Select in MV-Übersicht für Bulk-Aktionen (Neu prüfen)
  const [selectedIds, setSelectedIds] = useState(new Set());
  const [recheckRunning, setRecheckRunning] = useState(false);
  const toggleSelect = (id) => {
    setSelectedIds((s) => {
      const n = new Set(s);
      if (n.has(id)) n.delete(id); else n.add(id);
      return n;
    });
  };
  const toggleSelectAll = (ids) => {
    setSelectedIds((s) => {
      const allSelected = ids.every((id) => s.has(id));
      const n = new Set(s);
      if (allSelected) { ids.forEach((id) => n.delete(id)); }
      else { ids.forEach((id) => n.add(id)); }
      return n;
    });
  };
  const bulkRecheck = async () => {
    if (selectedIds.size === 0) return;
    setRecheckRunning(true);
    try {
      const { data } = await api.post("/tenancies/bulk-recheck", {
        tenancy_ids: Array.from(selectedIds),
      });
      toast.success(
        `Neu geprüft: ${data.regenerated?.length || 0} MV${data.failed?.length ? ` · ${data.failed.length} Fehler` : ""}${data.bank_matched ? ` · ${data.bank_matched} Bank-Buchungen zugeordnet` : ""}`,
      );
      setSelectedIds(new Set());
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Fehler beim Neu-Prüfen");
    } finally {
      setRecheckRunning(false);
    }
  };
  // iter168 · Bulk-Versand Amtliche Dokumente
  const [bulkSendOpen, setBulkSendOpen] = useState(false);
  // Sprint 5b — erweiterte Filter
  const [search, setSearch] = useState("");
  const [filterPropertyId, setFilterPropertyId] = useState("");
  const [filterOwnerId, setFilterOwnerId] = useState("");
  const [filterBalance, setFilterBalance] = useState("all"); // all|positive|negative|zero
  const [filterRentMin, setFilterRentMin] = useState("");
  const [filterRentMax, setFilterRentMax] = useState("");
  const [filterVacancy, setFilterVacancy] = useState("all"); // all|occupied|vacant
  const [open, setOpen] = useState(false);
  const [deleteTarget, setDeleteTarget] = useState(null);
  const [hardDeleteTarget, setHardDeleteTarget] = useState(null);
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const handledEditRef = useRef(false);

  // Bei expliziter Suche: „Nur aktive“ aufheben, damit auch künftige/beendete MVs gefunden werden
  const searching = search.trim().length > 0 && statusFilter === "active_only";
  const effStatus = searching ? "all" : statusFilter;
  const load = async () => {
    const params = {};
    const statusFilter = effStatus;
    if (statusFilter === "active_only") {
      // 2026-02 — Fix: „aktive Mietverhältnisse" heißt wirklich nur aktive,
      // NICHT „alles außer archiviert" (das hat auch beendete/terminierte
      // Verträge angezeigt).
      params.status = "active";
    } else if (statusFilter === "cancelled" || statusFilter === "ended") {
      // iter168 · Kündigungsstatus-Filter: alle laden, clientseitig auf
      // termination_status filtern (Backend kennt nur status).
      params.status = "all";
    } else if (statusFilter !== "all") {
      params.status = statusFilter;
    }
    const { data } = await api.get("/tenancies", { params });
    if (statusFilter === "cancelled") {
      setItems(data.filter((t) => t.termination_status === "cancelled"));
    } else if (statusFilter === "ended") {
      setItems(data.filter((t) => t.termination_status === "ended"));
    } else {
      setItems(data);
    }
  };

  const loadMeta = async () => {
    // Kontakte OHNE role-Filter laden, damit alle Kontakte (auch Interessenten,
    // frisch angelegte Kontakte ohne Rolle) als Mieter auswählbar sind.
    // Beim Speichern des MV wird die Rolle "tenant" automatisch ergänzt, falls sie fehlt.
    const [{ data: p }, { data: c }] = await Promise.all([
      api.get("/properties"),
      api.get("/contacts", { params: { limit: 1000 } }),
    ]);
    setProperties(p);
    setContacts(c);
  };

  useEffect(() => { load(); /* eslint-disable-next-line */ }, [effStatus]);
  useEffect(() => { loadMeta(); }, []);

  // Auto-Open Edit Dialog wenn ?edit={id} im URL Param (z. B. Sprung aus TenancyDetail "Bearbeiten")
  const returnToRef = useRef(null);
  useEffect(() => {
    const editId = searchParams.get("edit");
    const returnTo = searchParams.get("returnTo");
    const prefillRaw = searchParams.get("prefill");
    if (prefillRaw && !handledEditRef.current) {
      handledEditRef.current = true;
      try { setPrefill(JSON.parse(decodeURIComponent(escape(atob(prefillRaw))))); setEditing(null); setOpen(true); } catch { /* ungültig */ }
      navigate("/mietverhaeltnisse", { replace: true });
      return;
    }
    if (!editId || handledEditRef.current) return;
    handledEditRef.current = true;
    returnToRef.current = returnTo || null;
    (async () => {
      try {
        const { data } = await api.get(`/tenancies/${editId}`);
        setEditing(data);
        setOpen(true);
        navigate("/mietverhaeltnisse", { replace: true });
      } catch {
        toast.error("Mietverhältnis nicht gefunden");
      }
    })();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const confirmDelete = async () => {
    if (!deleteTarget) return;
    try {
      await api.delete(`/tenancies/${deleteTarget.id}`);
      toast.success("Mietverhältnis archiviert");
      setDeleteTarget(null);
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Fehler beim Löschen");
    }
  };

  // Sprint 5e — Endgültiges Löschen aus dem Archiv (nur Admins)
  const confirmHardDelete = async () => {
    if (!hardDeleteTarget) return;
    try {
      const { data } = await api.delete(`/tenancies/${hardDeleteTarget.id}/hard-delete`);
      toast.success(
        `Endgültig gelöscht. Vertragsnummer ${data?.contract_number_freed || hardDeleteTarget.contract_number} wieder verfügbar.`
      );
      setHardDeleteTarget(null);
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Fehler beim endgültigen Löschen");
    }
  };

  const { sorted, SortableTh } = useTableSort(items, "contract_number", "asc", {
    property: (t) => t.property_name || "",
    tenant: (t) => (t.tenant_names || []).join(", "),
    contract_start: (t) => t.contract_start || "",
    rent_cold: (t) => Number(t.rent_cold) || 0,
    balance: (t) => Number(t.balance) || 0,
    status: (t) => t.status || "",
  });

  // Sprint 5b — Client-Side Filtering nach Suchfeld + Filterchips.
  const propertyById = useMemo(() => Object.fromEntries(properties.map((p) => [p.id, p])), [properties]);
  // 2026-08 · Sortiere Objekte in Auswahl-Listen alphabetisch nach property_number
  const propertiesSorted = useMemo(
    () => [...properties].sort((a, b) => {
      const na = (a.property_number || "").toString();
      const nb = (b.property_number || "").toString();
      return na.localeCompare(nb, "de", { numeric: true, sensitivity: "base" });
    }),
    [properties],
  );
  const filtered = useMemo(() => {
    const q = search.trim().toLowerCase();
    const todayISO = new Date().toISOString().slice(0, 10);
    return sorted.filter((t) => {
      if (q) {
        const hay = [
          t.contract_number, t.property_name, t.unit_label,
          ...(t.tenant_names || []),
        ].join(" ").toLowerCase();
        if (!hay.includes(q)) return false;
      }
      // iter162 · „Nur aktive": Auch MVs mit contract_end in der Vergangenheit
      // ausblenden. Der Backend-Filter status=active fängt das nicht ab, weil
      // solche Verträge oft noch auf status='active' stehen, obwohl der
      // Vertrag längst abgelaufen ist.
      if (effStatus === "active_only" && t.contract_end && t.contract_end < todayISO) {
        return false;
      }
      if (filterPropertyId && t.property_id !== filterPropertyId) return false;
      if (filterOwnerId) {
        const prop = propertyById[t.property_id];
        if (!prop || prop.owner_id !== filterOwnerId) return false;
      }
      if (filterBalance !== "all") {
        const b = Number(t.balance) || 0;
        if (filterBalance === "positive" && b <= 0) return false;
        if (filterBalance === "negative" && b >= 0) return false;
        if (filterBalance === "zero" && b !== 0) return false;
      }
      if (filterRentMin !== "" && Number(t.rent_cold) < Number(filterRentMin)) return false;
      if (filterRentMax !== "" && Number(t.rent_cold) > Number(filterRentMax)) return false;
      if (filterVacancy === "occupied" && (t.tenant_names || []).length === 0) return false;
      if (filterVacancy === "vacant" && (t.tenant_names || []).length > 0) return false;
      return true;
    });
  }, [sorted, search, effStatus, filterPropertyId, filterOwnerId, filterBalance, filterRentMin, filterRentMax, filterVacancy, propertyById]);

  const activeChips = [];
  if (filterPropertyId) activeChips.push({ key: "prop", label: `Objekt: ${propertyById[filterPropertyId]?.property_number || "—"}`, clear: () => setFilterPropertyId("") });
  if (filterOwnerId) {
    const ownerName = (properties.find((p) => p.owner_id === filterOwnerId)?.owner_name) || filterOwnerId.slice(0, 6);
    activeChips.push({ key: "owner", label: `Eigentümer: ${ownerName}`, clear: () => setFilterOwnerId("") });
  }
  if (filterBalance !== "all") activeChips.push({ key: "bal", label: `Saldo: ${{positive:"positiv",negative:"offen",zero:"ausgeglichen"}[filterBalance]}`, clear: () => setFilterBalance("all") });
  if (filterRentMin !== "") activeChips.push({ key: "rmin", label: `Miete ≥ ${filterRentMin}€`, clear: () => setFilterRentMin("") });
  if (filterRentMax !== "") activeChips.push({ key: "rmax", label: `Miete ≤ ${filterRentMax}€`, clear: () => setFilterRentMax("") });
  if (filterVacancy !== "all") activeChips.push({ key: "vac", label: filterVacancy === "occupied" ? "Bewohnt" : "Leerstand", clear: () => setFilterVacancy("all") });

  const distinctOwners = useMemo(() => {
    const m = new Map();
    properties.forEach((p) => { if (p.owner_id) m.set(p.owner_id, p.owner_name || p.owner_id.slice(0, 6)); });
    return Array.from(m.entries());
  }, [properties]);

  const clearAllFilters = () => {
    setSearch(""); setFilterPropertyId(""); setFilterOwnerId("");
    setFilterBalance("all"); setFilterRentMin(""); setFilterRentMax(""); setFilterVacancy("all");
  };

  return (
    <div className="space-y-4" data-testid="tenancies-page">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-semibold tracking-tight text-slate-900">Mietverhältnisse</h1>
          <p className="text-sm text-slate-500 mt-1">Verträge, Mieter und Mietkonten</p>
        </div>
        <Dialog open={open} onOpenChange={(o) => { setOpen(o); if (!o) setEditing(null); }}>
          <DialogTrigger asChild>
            <Button className="bg-slate-900 hover:bg-slate-800 gap-2" data-testid="new-tenancy-btn" onClick={() => setEditing(null)}>
              <Plus size={16} /> Neues Mietverhältnis
            </Button>
          </DialogTrigger>
          <TenancyDialog key={`${open}-${editing?.id || "new"}`} editing={editing} prefill={editing ? null : prefill} properties={properties} contacts={contacts} reloadContacts={loadMeta} onSaved={() => {
            // 2026-08 (iter104) — Edit-Sprung-Bug fix:
            // returnTo zuerst auslesen und Navigation via microtask feuern, damit
            // die Router-Navigation nicht durch das Dialog-Close-Batching überschrieben wird.
            const target = returnToRef.current;
            returnToRef.current = null;
            setOpen(false); setEditing(null);
            if (target) {
              // navigate microtask-verzögert → Dialog-Animation + State-Updates schließen zuerst ab
              setTimeout(() => navigate(target), 0);
            } else {
              load();
            }
          }} />
        </Dialog>
      </div>

      <div className="bg-white border border-slate-200 rounded-sm p-3 space-y-2" data-testid="tenancy-filter-bar">
        <div className="flex items-center gap-2 flex-wrap">
          <Input
            placeholder="Suchen: Mieter, Vertragsnr., Objekt, Einheit…"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            className="h-9 max-w-sm"
            data-testid="tenancy-search"
          />
          {searching && <span className="text-[11px] text-sky-700" data-testid="tenancy-search-all-hint">Suche umfasst alle Mietverhältnisse (auch künftige & beendete)</span>}
          <Select value={statusFilter} onValueChange={setStatusFilter}>
            <SelectTrigger className="w-44 h-9" data-testid="tenancy-status-filter"><SelectValue /></SelectTrigger>
            <SelectContent>
              <SelectItem value="active_only">Status: Aktive</SelectItem>
              <SelectItem value="all">Status: Alle</SelectItem>
              {/* iter168 · Explizite Kündigungs-Filter */}
              <SelectItem value="cancelled">Status: Gekündigt (offen)</SelectItem>
              <SelectItem value="ended">Status: Beendet</SelectItem>
              {Object.entries(TENANCY_STATUS).map(([k, v]) => (
                <SelectItem key={k} value={k}>Status: {v.label}</SelectItem>
              ))}
            </SelectContent>
          </Select>
          <Select value={filterPropertyId || "_all"} onValueChange={(v) => setFilterPropertyId(v === "_all" ? "" : v)}>
            <SelectTrigger className="w-52 h-9" data-testid="tenancy-property-filter"><SelectValue placeholder="Objekt" /></SelectTrigger>
            <SelectContent>
              <SelectItem value="_all">Alle Objekte</SelectItem>
              {propertiesSorted.map((p) => (
                <SelectItem key={p.id} value={p.id}>{p.property_number} · {p.name}</SelectItem>
              ))}
            </SelectContent>
          </Select>
          {distinctOwners.length > 0 && (
            <Select value={filterOwnerId || "_all"} onValueChange={(v) => setFilterOwnerId(v === "_all" ? "" : v)}>
              <SelectTrigger className="w-48 h-9" data-testid="tenancy-owner-filter"><SelectValue placeholder="Eigentümer" /></SelectTrigger>
              <SelectContent>
                <SelectItem value="_all">Alle Eigentümer</SelectItem>
                {distinctOwners.map(([id, name]) => (
                  <SelectItem key={id} value={id}>{name}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          )}
          <Select value={filterBalance} onValueChange={setFilterBalance}>
            <SelectTrigger className="w-44 h-9" data-testid="tenancy-balance-filter"><SelectValue /></SelectTrigger>
            <SelectContent>
              <SelectItem value="all">Saldo: Alle</SelectItem>
              <SelectItem value="positive">Saldo: Im Plus</SelectItem>
              <SelectItem value="negative">Saldo: Offen / Minus</SelectItem>
              <SelectItem value="zero">Saldo: Ausgeglichen</SelectItem>
            </SelectContent>
          </Select>
          <Select value={filterVacancy} onValueChange={setFilterVacancy}>
            <SelectTrigger className="w-40 h-9" data-testid="tenancy-vacancy-filter"><SelectValue /></SelectTrigger>
            <SelectContent>
              <SelectItem value="all">Belegung: Alle</SelectItem>
              <SelectItem value="occupied">Bewohnt</SelectItem>
              <SelectItem value="vacant">Leerstand</SelectItem>
            </SelectContent>
          </Select>
          <div className="flex items-center gap-1 ml-1">
            <Input type="number" min="0" placeholder="Miete min €" value={filterRentMin} onChange={(e) => setFilterRentMin(e.target.value)} className="h-9 w-28" data-testid="tenancy-rent-min" />
            <span className="text-slate-400 text-xs">–</span>
            <Input type="number" min="0" placeholder="max €" value={filterRentMax} onChange={(e) => setFilterRentMax(e.target.value)} className="h-9 w-24" data-testid="tenancy-rent-max" />
          </div>
          <div className="text-xs text-slate-500 ml-auto">{filtered.length} von {items.length}</div>
        </div>
        {activeChips.length > 0 && (
          <div className="flex items-center gap-1.5 flex-wrap pt-1 border-t border-slate-100" data-testid="tenancy-active-chips">
            <span className="text-[10px] uppercase tracking-wider text-slate-500 font-semibold mr-1">Aktive Filter:</span>
            {activeChips.map((c) => (
              <button
                key={c.key} onClick={c.clear}
                className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full bg-slate-900 text-white text-[11px] hover:bg-slate-700"
                data-testid={`tenancy-chip-${c.key}`}
              >
                {c.label} <span className="text-slate-300">×</span>
              </button>
            ))}
            <button onClick={clearAllFilters} className="text-[11px] text-slate-500 hover:text-slate-800 underline ml-1" data-testid="tenancy-clear-filters">
              Alle entfernen
            </button>
          </div>
        )}
      </div>

      <div className="bg-white border border-slate-200 rounded-sm overflow-hidden">
        {/* Mobile: Karten-Ansicht */}
        <div className="md:hidden divide-y divide-slate-100" data-testid="tenancies-mobile-list">
          {filtered.length === 0 && <div className="p-6 text-center text-slate-500 text-sm">Keine Mietverhältnisse.</div>}
          {filtered.map((t) => {
            const s = TENANCY_STATUS[t.status] || { label: t.status, className: "bg-slate-100" };
            return (
              <Link
                key={t.id}
                to={`/mietverhaeltnisse/${t.id}`}
                className="block p-3 active:bg-slate-50"
                data-testid={`tenancy-card-${t.id}`}
              >
                <div className="flex items-start justify-between gap-2 mb-1.5">
                  <div className="font-mono text-xs text-slate-500">{t.contract_number}</div>
                  <span className={`text-[10px] px-2 py-0.5 rounded-full whitespace-nowrap ${s.className}`}>{s.label}</span>
                </div>
                <div className="text-sm font-semibold text-slate-900 mb-0.5">
                  {(t.tenant_names || []).join(", ") || "— Leerstand —"}
                </div>
                <div className="text-xs text-slate-700">{t.property_name}</div>
                <div className="text-xs text-slate-500 font-mono mb-2">
                  {t.unit_label}
                  {(t.additional_unit_labels || []).length > 0 && (
                    <span className="ml-1.5 inline-flex flex-wrap gap-1" data-testid={`tenancy-card-add-units-${t.id}`}>
                      {t.additional_unit_labels.map((lab, i) => (
                        <span key={i} className="px-1.5 py-0.5 rounded-sm bg-indigo-50 text-indigo-700 text-[9px] font-medium normal-case">
                          + {lab}
                        </span>
                      ))}
                    </span>
                  )}
                </div>
                <div className="flex items-center justify-between gap-3 text-xs">
                  <div className="text-slate-600">
                    Seit <span className="tabular-nums">{formatDate(t.contract_start)}</span>
                  </div>
                  <div className="text-right">
                    <div className="text-slate-500 text-[10px]">Saldo</div>
                    <div className={`tabular-nums font-medium ${t.balance < 0 ? "text-rose-700" : "text-emerald-700"}`}>
                      {formatEUR(t.balance)}
                    </div>
                  </div>
                </div>
              </Link>
            );
          })}
        </div>

        {/* Desktop: Tabellen-Ansicht */}
        <table className="w-full text-sm hidden md:table">
          <thead className="bg-slate-50 text-slate-700 text-left">
            <tr>
              <Th className="w-10 pl-3">
                <input
                  type="checkbox"
                  className="h-3.5 w-3.5 rounded border-slate-300 text-teal-600 focus:ring-teal-500"
                  checked={filtered.length > 0 && filtered.every((t) => selectedIds.has(t.id))}
                  onChange={() => toggleSelectAll(filtered.map((t) => t.id))}
                  data-testid="tenancy-select-all"
                  title="Alle sichtbaren auswählen"
                />
              </Th>
              <SortableTh field="contract_number">Vertragsnr.</SortableTh>
              <SortableTh field="property">Objekt / Einheit</SortableTh>
              <SortableTh field="tenant">Mieter</SortableTh>
              <SortableTh field="contract_start">Beginn</SortableTh>
              <SortableTh field="rent_cold" align="right" className="text-right">Kaltmiete</SortableTh>
              <SortableTh field="balance" align="right" className="text-right">Saldo</SortableTh>
              <SortableTh field="status">Status</SortableTh>
              <Th className="w-32 text-right">Aktionen</Th>
            </tr>
          </thead>
          <tbody>
            {filtered.length === 0 && <tr><td colSpan={9} className="p-6 text-center text-slate-500">Keine Mietverhältnisse.</td></tr>}
            {filtered.map((t) => {
              const s = TENANCY_STATUS[t.status] || { label: t.status, className: "bg-slate-100" };
              return (
                <tr key={t.id}
                    className={
                      "border-t border-slate-100 hover:bg-slate-50 cursor-pointer " +
                      (selectedIds.has(t.id) ? "bg-teal-50/60" : "")
                    }
                    data-testid={`tenancy-row-${t.id}`}
                    onClick={() => navigate(`/mietverhaeltnisse/${t.id}`)}
                >
                  <td className="pl-3 py-2 align-middle" onClick={(e) => e.stopPropagation()}>
                    <input
                      type="checkbox"
                      className="h-3.5 w-3.5 rounded border-slate-300 text-teal-600 focus:ring-teal-500"
                      checked={selectedIds.has(t.id)}
                      onChange={() => toggleSelect(t.id)}
                      data-testid={`tenancy-select-${t.id}`}
                    />
                  </td>
                  <Td className="font-mono text-slate-900">{t.contract_number}</Td>
                  <Td>
                    <div className="text-slate-900">{t.property_name}</div>
                    <div className="text-xs text-slate-500 font-mono flex items-center gap-1 flex-wrap">
                      <span>{t.unit_label}</span>
                      {(t.additional_unit_labels || []).map((lab, i) => (
                        <span key={i} className="px-1.5 py-0.5 rounded-sm bg-indigo-50 text-indigo-700 text-[10px] font-medium normal-case" data-testid={`tenancy-row-add-unit-${t.id}-${i}`}>
                          + {lab}
                        </span>
                      ))}
                    </div>
                  </Td>
                  <Td>{(t.tenant_names || []).join(", ") || "—"}</Td>
                  <Td className="tabular-nums">
                    {formatDate(t.contract_start)}
                    {(() => {
                      // iter142 — Hinweis-Badge, wenn MV noch nicht gestartet
                      const start = t.handover_date || t.contract_start;
                      const today = new Date().toISOString().slice(0, 10);
                      if (!start || String(start) <= today) return null;
                      if (t.termination_status === "cancelled" || t.termination_status === "ended") return null;
                      return (
                        <span className="ml-2 text-[10px] px-1.5 py-0.5 rounded-full bg-sky-100 text-sky-800 font-semibold whitespace-nowrap" data-testid={`tenancy-future-badge-${t.id}`}>
                          Zukünftig
                        </span>
                      );
                    })()}
                  </Td>
                  <Td className="text-right tabular-nums">{formatEUR(t.current_rent_cold ?? t.rent_cold)}</Td>
                  <Td className={`text-right tabular-nums font-medium ${t.balance < 0 ? "text-rose-700" : "text-emerald-700"}`}>
                    {formatEUR(t.balance)}
                  </Td>
                  <Td><span className={`text-[10px] px-2 py-0.5 rounded-full ${s.className}`}>{s.label}</span></Td>
                  <td className="px-3 py-2 text-right align-middle" onClick={(e) => e.stopPropagation()}>
                    <div className="flex justify-end gap-1">
                      <Button size="icon" variant="ghost" className="h-7 w-7" title="Bearbeiten"
                        onClick={async () => {
                          const { data } = await api.get(`/tenancies/${t.id}`);
                          setEditing(data); setOpen(true);
                        }} data-testid={`tenancy-edit-${t.id}`}>
                        <PencilSimple size={14} />
                      </Button>
                      <Button size="icon" variant="ghost" className="h-7 w-7 hover:text-rose-700" title="Löschen / Archivieren"
                        onClick={() => setDeleteTarget(t)}
                        data-testid={`tenancy-del-${t.id}`}>
                        <Trash size={14} />
                      </Button>
                      {isAdmin && t.status === "archived" && (
                        <Button size="icon" variant="ghost" className="h-7 w-7 text-rose-700 hover:text-rose-900 hover:bg-rose-50"
                          title="Endgültig löschen (Admin) — Vertragsnummer wird wieder frei"
                          onClick={() => setHardDeleteTarget(t)}
                          data-testid={`tenancy-hard-del-${t.id}`}>
                          <Trash size={14} weight="fill" />
                        </Button>
                      )}
                      <Link to={`/mietverhaeltnisse/${t.id}`} data-testid={`tenancy-open-${t.id}`}>
                        <Button size="icon" variant="ghost" className="h-7 w-7"><ArrowRight size={14} /></Button>
                      </Link>
                    </div>
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>

      {/* iter162 — Sticky-Bulk-Bar bei Multi-Auswahl */}
      {selectedIds.size > 0 && (
        <div
          className="sticky bottom-3 z-30 mx-auto max-w-2xl flex items-center justify-between gap-3 rounded-lg border border-slate-800 bg-slate-900 text-slate-50 px-4 py-2.5 shadow-lg"
          data-testid="tenancy-bulk-bar"
        >
          <div className="text-sm">
            <span className="font-semibold">{selectedIds.size}</span> Mietverhältnis{selectedIds.size === 1 ? "" : "se"} ausgewählt
          </div>
          <div className="flex items-center gap-2">
            <Button
              size="sm"
              variant="outline"
              className="h-8 gap-1.5 bg-transparent border-slate-500 text-slate-100 hover:bg-slate-800"
              onClick={() => setSelectedIds(new Set())}
              data-testid="tenancy-bulk-clear"
            >
              Auswahl aufheben
            </Button>
            <Button
              size="sm"
              className="h-8 gap-1.5 bg-teal-700 hover:bg-teal-800 text-white"
              onClick={bulkRecheck}
              disabled={recheckRunning}
              data-testid="tenancy-bulk-recheck"
              title="Sollstellungen neu generieren und Bank-Buchungen automatisch zuordnen"
            >
              {recheckRunning ? "Läuft…" : "Neu prüfen"}
            </Button>
            <Button
              size="sm"
              className="h-8 gap-1.5 bg-slate-700 hover:bg-slate-600 text-white"
              onClick={() => setBulkSendOpen(true)}
              data-testid="tenancy-bulk-send"
              title="Amtliches Dokument an alle ausgewählten Mietverhältnisse senden"
            >
              Massenversand
            </Button>
          </div>
        </div>
      )}
      {/* iter168 · Bulk-Versand-Dialog */}
      <BulkSendDialog
        open={bulkSendOpen}
        onClose={() => setBulkSendOpen(false)}
        tenancyIds={Array.from(selectedIds)}
        onDone={() => { setSelectedIds(new Set()); load(); }}
      />

      {/* Professioneller Löschen-Bestätigungs-Dialog */}
      <AlertDialog open={!!deleteTarget} onOpenChange={(o) => !o && setDeleteTarget(null)}>
        <AlertDialogContent data-testid="tenancy-delete-confirm-dialog">
          <AlertDialogHeader>
            <div className="w-11 h-11 rounded-full bg-rose-50 flex items-center justify-center mb-1">
              <Trash size={20} className="text-rose-600" />
            </div>
            <AlertDialogTitle>Mietverhältnis wirklich löschen?</AlertDialogTitle>
            <AlertDialogDescription>
              {deleteTarget && (
                <>
                  Das Mietverhältnis{" "}
                  <span className="font-mono font-medium text-slate-900">{deleteTarget.contract_number}</span>{" "}
                  mit{" "}
                  <span className="font-medium text-slate-900">{deleteTarget.tenant_names?.join(" & ") || "—"}</span>{" "}
                  wird in das Archiv verschoben. Die Einheit{" "}
                  <span className="font-mono font-medium text-slate-900">{deleteTarget.unit_label}</span>{" "}
                  wird wieder als leerstehend markiert. Buchungen und Dokumente bleiben unverändert erhalten.
                </>
              )}
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel data-testid="tenancy-delete-cancel">Abbrechen</AlertDialogCancel>
            <AlertDialogAction
              onClick={confirmDelete}
              className="bg-rose-600 hover:bg-rose-700 text-white"
              data-testid="tenancy-delete-confirm-btn"
            >
              <Trash size={14} className="mr-1.5" /> Endgültig archivieren
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      {/* Sprint 5e — Hard-Delete (Admin) für archivierte MV */}
      <AlertDialog open={!!hardDeleteTarget} onOpenChange={(o) => !o && setHardDeleteTarget(null)}>
        <AlertDialogContent data-testid="tenancy-hard-delete-dialog">
          <AlertDialogHeader>
            <div className="w-11 h-11 rounded-full bg-rose-100 flex items-center justify-center mb-1">
              <Trash size={20} className="text-rose-700" weight="fill" />
            </div>
            <AlertDialogTitle>Endgültig löschen?</AlertDialogTitle>
            <AlertDialogDescription>
              {hardDeleteTarget && (
                <>
                  Das archivierte Mietverhältnis{" "}
                  <span className="font-mono font-medium text-slate-900">{hardDeleteTarget.contract_number}</span>{" "}
                  mit{" "}
                  <span className="font-medium text-slate-900">{hardDeleteTarget.tenant_names?.join(" & ") || "—"}</span>{" "}
                  wird inklusive aller zugehörigen Sollstellungen und Buchungsregeln <span className="font-semibold text-rose-700">unwiderruflich</span> gelöscht.
                  Die Vertragsnummer wird wieder freigegeben und kann neu vergeben werden.
                  <span className="block mt-2 text-[12px] text-rose-700 font-semibold">
                    Diese Aktion kann NICHT rückgängig gemacht werden.
                  </span>
                </>
              )}
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel data-testid="tenancy-hard-delete-cancel">Abbrechen</AlertDialogCancel>
            <AlertDialogAction
              onClick={confirmHardDelete}
              className="bg-rose-700 hover:bg-rose-800 text-white"
              data-testid="tenancy-hard-delete-confirm-btn"
            >
              <Trash size={14} weight="fill" className="mr-1.5" /> Endgültig löschen
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}

function Th({ children, className = "" }) {
  return <th className={`px-3 py-2.5 text-xs font-semibold uppercase tracking-wider text-slate-600 ${className}`}>{children}</th>;
}
function Td({ children, className = "" }) {
  return <td className={`px-3 py-2.5 align-middle ${className}`}>{children}</td>;
}

export function TenancyDialog({ properties, contacts, onSaved, reloadContacts, editing, prefill }) {
  // 2026-08 · Objekte alphabetisch nach property_number sortiert
  const propertiesSorted = useMemo(
    () => [...properties].sort((a, b) => {
      const na = (a.property_number || "").toString();
      const nb = (b.property_number || "").toString();
      return na.localeCompare(nb, "de", { numeric: true, sensitivity: "base" });
    }),
    [properties],
  );
  const [propertyId, setPropertyId] = useState(editing?.property_id || prefill?.property_id || "");
  const [units, setUnits] = useState([]);
  const [selectedProp, setSelectedProp] = useState(null);
  const [contractPreview, setContractPreview] = useState(null);
  const [tenantDialogOpen, setTenantDialogOpen] = useState(false);
  const [openEnd, setOpenEnd] = useState(!editing?.contract_end);
  const [tenantSearch, setTenantSearch] = useState("");
  const [form, setForm] = useState(editing ? {
    unit_id: editing.unit_id || "",
    additional_unit_ids: editing.additional_unit_ids || [],
    unit_grouping: editing.unit_grouping || "annex",
    additional_unit_rents: editing.additional_unit_rents || {},
    tenant_contact_ids: editing.tenant_contact_ids || [],
    tenancy_type: editing.tenancy_type || "01",
    contract_start: editing.contract_start || new Date().toISOString().slice(0, 10),
    contract_end: editing.contract_end || "",
    termination_notice_months: editing.termination_notice_months || 3,
    minimum_term_months: editing.minimum_term_months ?? "",
    auto_renew_months: editing.auto_renew_months ?? "",
    handover_date: editing.handover_date || "",
    handover_time: editing.handover_time || "",
    handover_location: editing.handover_location || "",
    return_date: editing.return_date || "",
    return_time: editing.return_time || "",
    return_location: editing.return_location || "",
    rent_cold: editing.rent_cold || 0,
    utilities_advance: editing.utilities_advance || 0,
    heating_advance: editing.heating_advance || 0,
    parking_rent: editing.parking_rent || 0,
    surcharges: editing.surcharges || [],
    vat_rate: editing.vat_rate ?? "",
    deposit: editing.deposit || 0,
    deposit_status: editing.deposit_status && editing.deposit_status !== "pending" ? editing.deposit_status : "open",
    deposit_type: editing.deposit_type || "cash",
    deposit_provider: editing.deposit_provider || "",
    deposit_document_received: !!editing.deposit_document_received,
    payment_rhythm: editing.payment_rhythm || "monthly",
    due_day: editing.due_day || 3,
    due_day_mode: editing.due_day_mode || "workday",
    due_day_region: editing.due_day_region || "NW",
    sepa_mandate: !!editing.sepa_mandate,
    prorate_first_month: editing.prorate_first_month !== undefined ? !!editing.prorate_first_month : true,
    surplus_offset_mode: editing.surplus_offset_mode || "manual",
    first_month_custom: editing.first_month_custom || null,
    rent_type: editing.rent_type || "normal",
    index_base: editing.index_base || "",
    index_base_date: editing.index_base_date || "",
    index_threshold_pct: editing.index_threshold_pct ?? "",
    persons_count: editing.persons_count ?? "",
    status: editing.status || "active",
    update_tenant_address: false,
    notes: editing.notes || "",
    // Sprint 5a/5e — Felder beim Editieren ebenfalls aus DB übernehmen (Bug-Fix BK-Gesamt)
    bk_total_only: !!editing.bk_total_only,
    utilities_total: editing.utilities_total ?? "",
    ordinary_termination_waiver: !!editing.ordinary_termination_waiver,
    auto_create_postings: true, // bei Edit irrelevant; Sollstellungen wurden bereits erzeugt
    contract_living_area: editing.contract_living_area ?? "",
  } : {
    unit_id: "",
    additional_unit_ids: [],
    unit_grouping: "annex",
    additional_unit_rents: {},
    tenant_contact_ids: [],
    tenancy_type: "01",
    contract_start: new Date().toISOString().slice(0, 10),
    contract_end: "",
    termination_notice_months: 3,
    minimum_term_months: "",
    auto_renew_months: "",
    handover_date: "",
    handover_time: "",
    return_date: "",
    return_time: "",
    rent_cold: 0, utilities_advance: 0, heating_advance: 0, parking_rent: 0, surcharges: [],
    vat_rate: "",
    deposit: 0, deposit_status: "open", deposit_type: "cash", deposit_provider: "", deposit_document_received: false,
    payment_rhythm: "monthly",
    due_day: 3, due_day_mode: "workday", due_day_region: "NW", sepa_mandate: false,
    prorate_first_month: true,
    first_month_custom: null,
    rent_type: "normal",
    index_base: "", index_base_date: "", index_threshold_pct: "",
    persons_count: "",
    status: "active",
    update_tenant_address: false,
    // Sprint 5a
    bk_total_only: false,
    utilities_total: "",
    ordinary_termination_waiver: false,
    auto_create_postings: true,
    // Sprint 5e — Im Mietvertrag abweichende Wohnungsgröße
    contract_living_area: "",
    // Sprint 5g — Mietkonto-Referenz (Bank-Konto des Eigentümers)
    rent_account_id: editing?.rent_account_id || "",
    notes: "",
  });
  // Übernahme aus der Vermarktung: Einheit, Mieter und Miete vorbelegen
  useEffect(() => {
    if (editing || !prefill) return;
    setForm((f) => ({ ...f, ...Object.fromEntries(Object.entries({ unit_id: prefill.unit_id, additional_unit_ids: (prefill.additional_unit_ids || []).length ? prefill.additional_unit_ids : undefined, tenant_contact_ids: prefill.tenant_contact_ids,
      rent_cold: prefill.rent_cold, utilities_advance: prefill.rent_utilities, heating_advance: prefill.rent_heating, contract_start: prefill.contract_start })
      .filter(([, v]) => v !== null && v !== undefined && v !== "")) }));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Sprint 5g — Bankkonten des Eigentümers (für Mietkonto-Picker)
  const [ownerBankAccounts, setOwnerBankAccounts] = useState([]);

  useEffect(() => {
    if (!propertyId) { setUnits([]); setSelectedProp(null); return; }
    api.get(`/properties/${propertyId}`)
      .then((r) => {
        setUnits(r.data.units || []);
        setSelectedProp(r.data);
      })
      .catch(() => { setUnits([]); setSelectedProp(null); });
  }, [propertyId]);

  // Sprint 5g / 5g-ext — Bankkonten ALLER Eigentümer nachladen (Primär + Miteigentümer),
  // sobald das Objekt geladen ist. Jedes Konto wird mit `owner_contact_id`+`owner_label`
  // dekoriert, damit die Auswahl transparent gruppiert werden kann.
  useEffect(() => {
    if (!selectedProp) { setOwnerBankAccounts([]); return; }
    const ownerIds = [];
    if (selectedProp.owner_contact_id) ownerIds.push(selectedProp.owner_contact_id);
    for (const s of (selectedProp.ownership_shares || [])) {
      if (s?.contact_id && !ownerIds.includes(s.contact_id)) ownerIds.push(s.contact_id);
    }
    // WEG-Fallback — Eigentümer der aktuell gewählten Einheit hinzunehmen
    const _unit = (units || []).find((u) => u.id === form.unit_id);
    if (_unit?.owner_contact_id && !ownerIds.includes(_unit.owner_contact_id)) {
      ownerIds.push(_unit.owner_contact_id);
    }
    if (ownerIds.length === 0) { setOwnerBankAccounts([]); return; }
    Promise.all(ownerIds.map(async (oid) => {
      let owner = contacts.find((c) => c.id === oid);
      if (!owner) {
        // Contacts noch nicht geladen oder Kontakt außerhalb der geladenen Liste
        try {
          const cr = await api.get(`/contacts/${oid}`);
          owner = cr.data;
        } catch { /* ignore */ }
      }
      const label = owner
        ? (owner.contact_type === "person"
            ? `${owner.first_name || ""} ${owner.last_name || ""}`.trim() || owner.company_name || "(unbenannt)"
            : owner.company_name || "(unbenannt)")
        : "Eigentümer";
      try {
        const r = await api.get(`/contacts/${oid}/bank-accounts`);
        return (r.data || []).map((a) => ({ ...a, owner_contact_id: oid, owner_label: label }));
      } catch { return []; }
    })).then((lists) => {
      const flat = lists.flat();
      setOwnerBankAccounts(flat);
      // Default: bereits gespeichertes rent_account_id belassen; sonst
      // Standard-Konto (is_default=true) des Primär-Eigentümers vorwählen,
      // fallback: erstes gefundenes Konto.
      setForm((s) => {
        if (s.rent_account_id) return s;
        const primary = selectedProp.owner_contact_id || _unit?.owner_contact_id;
        const def = flat.find((a) => a.is_default && a.owner_contact_id === primary)
          || flat.find((a) => a.is_default)
          || flat[0];
        return def ? { ...s, rent_account_id: def.id } : s;
      });
    });
  }, [selectedProp?.id, selectedProp?.owner_contact_id, form.unit_id, contacts.length]);

  useEffect(() => {
    if (!propertyId || !form.tenancy_type) { setContractPreview(null); return; }
    api.get("/tenancies/preview-number", { params: { property_id: propertyId, tenancy_type: form.tenancy_type, unit_id: form.unit_id || undefined } })
      .then((r) => setContractPreview(r.data))
      .catch(() => setContractPreview(null));
  }, [propertyId, form.tenancy_type, form.unit_id]);

  const selectedUnit = units.find((u) => u.id === form.unit_id);
  const ownerContact = (() => {
    // Miethaus/SEG: Eigentümer am Objekt
    if (selectedProp?.owner_contact_id) return contacts.find((c) => c.id === selectedProp.owner_contact_id) || null;
    // WEG-Fallback: Eigentümer an der Einheit
    if (selectedUnit?.owner_contact_id) return contacts.find((c) => c.id === selectedUnit.owner_contact_id) || null;
    return null;
  })();

  // VAT-Feld: iter159s — sichtbar für ALLE Vertragsarten (auch Wohnraum + Stellplatz),
  // damit der Vermieter auch bei Wohnraum-Mietverträgen mit Gewerbe-Anteil
  // (z. B. beigemieteter gewerblicher Stellplatz) USt. eintragen kann.
  // Bei Wohnraum default 0 %, sonst 19 %.
  const isVatType = true;
  const isCommercialType = form.tenancy_type && form.tenancy_type !== "01";
  useEffect(() => {
    setForm((s) => {
      if (form.tenancy_type === "01") {
        // Bei Wohnraum: Vorbelegung 0 %, aber User kann manuell erhöhen
        if (s.vat_rate === "" || s.vat_rate === null || s.vat_rate === undefined) return { ...s, vat_rate: 0 };
        return s;
      }
      if (s.vat_rate === "" || s.vat_rate === null || s.vat_rate === undefined) return { ...s, vat_rate: 19 };
      return s;
    });
    // eslint-disable-next-line
  }, [form.tenancy_type]);

  useEffect(() => {
    if (selectedUnit && UNIT_KIND_TO_TENANCY_TYPE[selectedUnit.unit_kind]) {
      setForm((s) => ({ ...s, tenancy_type: UNIT_KIND_TO_TENANCY_TYPE[selectedUnit.unit_kind] }));
    }
    if (selectedUnit) {
      setForm((s) => ({
        ...s,
        rent_cold: s.rent_cold || selectedUnit.default_rent_cold || 0,
        utilities_advance: s.utilities_advance || selectedUnit.default_utilities_advance || 0,
        heating_advance: s.heating_advance || selectedUnit.default_heating_advance || 0,
      }));
    }
  }, [selectedUnit]);

  // Sprint 5e — VPI-Wert beim Vertragsstart automatisch nachladen (Indexmiete)
  const [vpiHit, setVpiHit] = useState(null);
  useEffect(() => {
    if (form.rent_type !== "index" || !form.contract_start) { setVpiHit(null); return; }
    let cancel = false;
    api.get("/tenancies/lookup-vpi", { params: { date: form.contract_start } })
      .then(({ data }) => {
        if (cancel || !data?.found) return;
        setVpiHit(data);
        // Nur überschreiben, wenn das Feld noch leer ist (User-Eingabe respektieren)
        setForm((s) => (s.index_base === "" || s.index_base === null || s.index_base === undefined)
          ? { ...s, index_base: data.value } : s);
      })
      .catch(() => { if (!cancel) setVpiHit(null); });
    return () => { cancel = true; };
  }, [form.rent_type, form.contract_start]);

  // 2026-08 · Verfügbarkeitsprüfung: sobald Einheiten ausgewählt sind, das
  // früheste freie Datum abfragen und (falls > aktueller Vertragsbeginn)
  // automatisch als Vertragsbeginn vorschlagen. Der User kann jederzeit
  // überschreiben. Warnung wird als Banner im Vertragsdauer-Block gezeigt.
  const [availability, setAvailability] = useState(null);
  // Verbundene Einheiten: „nur gemeinsam“ automatisch hinzufügen
  useEffect(() => {
    if (editing || !form.unit_id) return;
    const u = units.find((x) => x.id === form.unit_id);
    const need = (u?.linked_units || []).filter((l) => l.rule === "together").map((l) => l.unit_id)
      .filter((id) => !(form.additional_unit_ids || []).includes(id) && units.some((x) => x.id === id));
    if (!need.length) return;
    setForm((s) => ({ ...s, additional_unit_ids: [...(s.additional_unit_ids || []), ...need] }));
    toast.info(`Verbundene Einheit${need.length > 1 ? "en" : ""} automatisch hinzugefügt (nur gemeinsam vermietbar)`);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [form.unit_id, units]);
  const availabilityUnitsKey = [form.unit_id, ...(form.additional_unit_ids || [])].filter(Boolean).sort().join(",");
  useEffect(() => {
    if (!availabilityUnitsKey) { setAvailability(null); return; }
    let cancel = false;
    const params = { unit_ids: availabilityUnitsKey };
    if (editing?.id) params.exclude_tenancy_id = editing.id;
    api.get("/tenancies/check-availability", { params }).then(({ data }) => {
      if (cancel) return;
      setAvailability(data);
      const earliest = data?.earliest_available_date;
      if (!editing && earliest && (form.contract_start || "") < earliest) {
        setForm((s) => ({ ...s, contract_start: earliest }));
      }
    }).catch(() => { if (!cancel) setAvailability(null); });
    return () => { cancel = true; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [availabilityUnitsKey]);

  const set = (k, v) => setForm((s) => ({ ...s, [k]: v }));
  // Sprint 5e — Prozent-Eingaben beim Blur normalisieren (5 → 5,0 → 5.0 intern)
  const setPercent = (k) => (e) => {
    const raw = (e.target.value || "").replace(",", ".").trim();
    if (raw === "") { set(k, ""); return; }
    const n = Number(raw);
    if (Number.isFinite(n)) set(k, n);
  };

  const rc = Number(form.rent_cold) || 0;
  // VM-11a — Wenn "Gesamte Betriebskosten" aktiv ist, wird utilities_total statt
  // utilities_advance + heating_advance in die Warmmieten-Summe eingerechnet.
  const utTotal = Number(form.utilities_total) || 0;
  const ua = form.bk_total_only ? utTotal : (Number(form.utilities_advance) || 0);
  const ha = form.bk_total_only ? 0 : (Number(form.heating_advance) || 0);
  const pr = Number(form.parking_rent) || 0;
  const vat = (Number(form.vat_rate) || 0) > 0 ? (Number(form.vat_rate) || 0) : 0;
  const vatFactor = 1 + vat / 100;
  const rcGross = rc * vatFactor;
  const uaGross = ua * vatFactor;
  const haGross = ha * vatFactor;
  const prGross = pr * vatFactor;

  const dep = Number(form.deposit) || 0;
  const depMin = rc * 2;
  const depMax = rc * 3;
  let depositHint = null;
  // 2026-08 · § 551 BGB gilt nur für Wohnraum-Mietverträge (Typ "01") sowie
  // die Wohnraum-Sonderformen Staffelmiete / Indexmiete (Typ "01" plus rent_type).
  // Für Gewerbe (Typ "02"), Stellplatz, Garage etc. gilt Vertragsfreiheit → keine
  // BGB-basierten Warn-Hinweise ausgeben (das wäre irreführend).
  const wohnraumRule = form.tenancy_type === "01";
  if (rc > 0 && dep > 0) {
    if (wohnraumRule) {
      if (dep < depMin) depositHint = { tone: "warning", msg: `Hinweis: Die Kaution darf nach §551 BGB mindestens 2 Kaltmieten (${formatEUR(depMin)}) und maximal 3 Kaltmieten (${formatEUR(depMax)}) betragen.` };
      else if (dep > depMax) depositHint = { tone: "warning", msg: `Hinweis: Die Kaution darf nach §551 BGB maximal 3 Kaltmieten (${formatEUR(depMax)}) betragen.` };
      else depositHint = { tone: "ok", msg: `Innerhalb des Rahmens (2–3 Kaltmieten: ${formatEUR(depMin)} – ${formatEUR(depMax)}).` };
    } else {
      // Gewerbe / Stellplatz / Garage: nur unverbindliche Empfehlung, kein „nach § 551 BGB"
      depositHint = { tone: "info", msg: `Empfehlung: Kaution zwischen 2–3 Kaltmieten (${formatEUR(depMin)} – ${formatEUR(depMax)}) — bei Gewerbemietverträgen gilt Vertragsfreiheit.` };
    }
  } else if (rc > 0) {
    if (wohnraumRule) {
      depositHint = { tone: "info", msg: `Empfehlung nach §551 BGB: 2–3 Kaltmieten (${formatEUR(depMin)} – ${formatEUR(depMax)}).` };
    } else {
      depositHint = { tone: "info", msg: `Empfehlung: 2–3 Kaltmieten (${formatEUR(depMin)} – ${formatEUR(depMax)}) — Gewerbe: Vertragsfreiheit, oft auch Bürgschaft möglich.` };
    }
  }

  const totalRentNet = rc + ua + ha + pr;
  const totalRentGross = rcGross + uaGross + haGross + prGross;

  // --- Mietersuche: Treffer-Filter ---
  // iter159s — Nur Kontakte mit Rolle "tenant" auswählbar (User-Wunsch:
  // "nur Kontakte mit Attribut Mieter"). Alte Kontakte ohne roles-Feld
  // bleiben sichtbar für Rückwärtskompatibilität.
  const tenantContacts = contacts.filter((c) => {
    const roles = Array.isArray(c.roles) ? c.roles : [];
    // Wenn roles gesetzt ist, muss "tenant" enthalten sein.
    // Wenn kein roles-Feld existiert (alt), Kontakt zulassen.
    return roles.length === 0 || roles.includes("tenant");
  });
  const searchLower = tenantSearch.trim().toLowerCase();
  const filteredContacts = !searchLower
    ? tenantContacts
    : tenantContacts.filter((c) => {
        const name = contactDisplayName(c).toLowerCase();
        // E-Mails können Strings ODER {email,label}-Objekte sein → sicher flatten
        const email = emailListToText(c.emails).toLowerCase();
        const phone = Array.isArray(c.phones) ? c.phones.join(" ").toLowerCase() : "";
        const zip = typeof c.address?.zip === "string" ? c.address.zip.toLowerCase() : "";
        const city = typeof c.address?.city === "string" ? c.address.city.toLowerCase() : "";
        return name.includes(searchLower) || email.includes(searchLower) || phone.includes(searchLower) || zip.includes(searchLower) || city.includes(searchLower);
      });
  const selectedTenants = (form.tenant_contact_ids || [])
    .map((id) => contacts.find((c) => c.id === id))
    .filter(Boolean);

  const submit = async (e) => {
    e.preventDefault();
    e.stopPropagation();
    if (!propertyId || !form.unit_id) { toast.error("Objekt und Einheit erforderlich"); return; }
    if ((form.tenant_contact_ids || []).length === 0) { toast.error("Mindestens ein Mieter erforderlich"); return; }
    try {
      const payload = {
        ...form,
        property_id: propertyId,
        additional_unit_ids: (form.additional_unit_ids || []).filter((id) => id && id !== form.unit_id),
        rent_cold: Number(form.rent_cold) || 0,
        // VM-11a — Bei "Gesamt-BK"-Modus wird utilities_total ins utilities_advance
        // gespiegelt (Backend + Sollstellungen kennen nur NK/HK-Felder), damit die
        // Warmmiete stimmt. bk_total_only markiert später die Anzeige als "gesamt".
        utilities_advance: form.bk_total_only
          ? (Number(form.utilities_total) || 0)
          : (Number(form.utilities_advance) || 0),
        heating_advance: form.bk_total_only ? 0 : (Number(form.heating_advance) || 0),
        parking_rent: Number(form.parking_rent) || 0,
        surcharges: (form.surcharges || []).filter((z) => (z.label || "").trim() && Number(z.amount) > 0)
          .map((z) => ({ label: z.label.trim(), amount: Number(z.amount) })),
        vat_rate: isVatType && form.vat_rate !== "" ? Number(form.vat_rate) : null,
        deposit: Number(form.deposit) || 0,
        deposit_type: form.deposit_type || "cash",
        deposit_provider: form.deposit_provider || null,
        deposit_document_received: !!form.deposit_document_received,
        due_day: Number(form.due_day) || 3,
        due_day_mode: form.due_day_mode || "workday",
        due_day_region: form.due_day_region || "NW",
        termination_notice_months: Number(form.termination_notice_months) || 3,
        minimum_term_months: form.minimum_term_months === "" ? null : Number(form.minimum_term_months),
        auto_renew_months: form.auto_renew_months === "" ? null : Number(form.auto_renew_months),
        persons_count: form.persons_count === "" ? null : Number(form.persons_count),
        keys_handed_over: null,
        contract_end: openEnd ? null : (form.contract_end || null),
        handover_date: form.handover_date || null,
        handover_time: form.handover_date && form.handover_time ? form.handover_time : null,
        handover_location: (form.handover_location || "").trim() || null,
        return_location: (form.return_location || "").trim() || null,
        return_date: form.return_date || null,
        return_time: form.return_date && form.return_time ? form.return_time : null,
        index_base: form.index_base === "" ? null : Number(form.index_base),
        index_base_date: form.index_base_date || null,
        index_threshold_pct: form.index_threshold_pct === "" ? null : Number(form.index_threshold_pct),
        sepa_mandate_reference: null,
        notes: form.notes || null,
        update_tenant_address: !!form.update_tenant_address && form.tenancy_type === "01",
        // Sprint 5a — neue Felder
        bk_total_only: !!form.bk_total_only,
        utilities_total: form.bk_total_only ? (Number(form.utilities_total) || 0) : null,
        ordinary_termination_waiver: !!form.ordinary_termination_waiver,
        auto_create_postings: form.auto_create_postings !== false,
        surplus_offset_mode: form.surplus_offset_mode || "manual",
        // Sprint 5e — Vom Mietvertrag abweichende Wohnungsgröße
        contract_living_area: form.contract_living_area === "" ? null : Number(form.contract_living_area),
        // Sprint 5g — Mietkonto-Auswahl (Snapshot IBAN/Bank/Inhaber wird backend-seitig gezogen)
        rent_account_id: form.rent_account_id || null,
      };
      if (editing) {
        const { data: patchRes } = await api.patch(`/tenancies/${editing.id}`, payload);
        // VM-9c — Feedback zur Adressänderung
        if (payload.update_tenant_address) {
          const n = patchRes?.addresses_changed || 0;
          const skipped = patchRes?.addresses_skipped_same || 0;
          if (n > 0) {
            toast.success(`Mietverhältnis aktualisiert · ${n} Mieter-Adresse(n) auf Objekt-Adresse gesetzt & archiviert`);
          } else if (skipped > 0) {
            toast.info(`Mietverhältnis aktualisiert · Adressen waren bereits identisch mit der Objekt-Adresse (${skipped} Mieter) — keine Archivierung nötig`);
          } else {
            toast.success("Mietverhältnis aktualisiert");
          }
        } else {
          toast.success("Mietverhältnis aktualisiert");
        }
        // iter159f — Wenn rent-relevante Felder geändert wurden, dem User
        // die Neu-Berechnung anbieten (inkl. optionalem Entfernen der
        // automatisch erzeugten Vor-Verwalter-Ausgleich-Buchungen).
        const rentChanged = patchRes?.rent_fields_changed || [];
        const hasVvaus = !!patchRes?.has_vorverwalter_ausgleich;
        if (rentChanged.length > 0) {
          const fieldLabels = {
            rent_cold: "Kaltmiete", utilities_advance: "NK-Vorauszahlung",
            heating_advance: "Heizkosten-VZ", parking_rent: "Stellplatz-Miete",
            other_payments: "Sonstiges", vat_rate: "USt-Satz",
            contract_start: "Vertragsbeginn", contract_end: "Vertragsende",
            payment_rhythm: "Zahlungsrhythmus", due_day: "Fälligkeitstag", due_day_mode: "Fälligkeits-Modus",
          };
          const labels = rentChanged.map((f) => fieldLabels[f] || f).join(", ");
          // Kaskadierte Bestätigung: erst Regenerierung, dann ggf. Vor-Verwalter-Cleanup
          const doRegen = await window._domivusConfirm(
            `Vertragsdaten geändert (${labels}).\n\n` +
            `Sollen die Sollstellungen JETZT neu berechnet werden?\n\n` +
            `OK = Ab heute neu berechnen (historische Werte bleiben)\n` +
            `Abbrechen = Keine Neu-Berechnung`
          );
          if (doRegen) {
            let removeVvaus = false;
            if (hasVvaus) {
              removeVvaus = await window._domivusConfirm(
                "Für dieses Mietverhältnis existieren automatisch erzeugte Vor-Verwalter-Ausgleich-Buchungen.\n\n" +
                "Sollen diese ebenfalls entfernt werden, damit sie nicht die neuen Sollstellungen verzerren?\n\n" +
                "OK = Ja, Vor-Verwalter-Ausgleich-Buchungen entfernen\n" +
                "Abbrechen = Nein, Ausgleich-Buchungen behalten"
              );
            }
            try {
              const params = new URLSearchParams();
              if (removeVvaus) params.set("remove_vorverwalter_ausgleich", "true");
              // iter159v — Bei Änderung des Vertragsbeginns oder -endes den
              // vollen historischen Range regenerieren (sonst fehlen die
              // Sollstellungen für den neuen Zeitraum).
              const historyRelevant = rentChanged.some((f) => f === "contract_start" || f === "contract_end");
              if (historyRelevant) params.set("include_history", "true");
              const url = `/tenancies/${editing.id}/regenerate-soll${params.toString() ? "?" + params.toString() : ""}`;
              const { data: regen } = await api.post(url);
              const msg = `Sollstellungen neu berechnet: ${regen.deleted} gelöscht · ${regen.created} neu`
                + (regen.deleted_ausgleich ? ` · ${regen.deleted_ausgleich} Vor-Verwalter-Ausgleich entfernt` : "");
              toast.success(msg);
              // iter159v — Retro-Bank-Match neu triggern, damit alte Bank-
              // Buchungen nach Verschiebung des Vertragsbeginns korrekt
              // zugeordnet werden.
              if (historyRelevant) {
                try {
                  const { data: rm } = await api.post(`/bank/rematch-unmatched`);
                  if (rm?.matched > 0) toast.success(`Bank: ${rm.matched} Umsatz/-e nachträglich zugeordnet`);
                } catch { /* stumm */ }
              }
            } catch (e2) {
              toast.error(e2?.response?.data?.detail || "Fehler bei der Neu-Berechnung");
            }
          }
        }
      } else {
        await api.post("/tenancies", payload);
        toast.success("Mietverhältnis angelegt");
      }
      onSaved();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Fehler");
    }
  };

  return (
    <DialogContent className="max-w-3xl max-h-[90vh] overflow-y-auto">
      <DialogHeader>
        <DialogTitle className="flex items-baseline justify-between gap-3 flex-wrap">
          <span>{editing ? "Mietverhältnis bearbeiten" : "Neues Mietverhältnis"}</span>
          {!editing && contractPreview?.preview && (
            <span className="text-sm font-mono text-slate-500" data-testid="contract-preview">
              Mietvertrags-Nr.: <span className="text-slate-900 font-semibold ml-1">{contractPreview.preview}</span>
            </span>
          )}
          {editing && (
            <span className="text-sm font-mono text-slate-500">
              Mietvertrags-Nr.: <span className="text-slate-900 font-semibold ml-1">{editing.contract_number}</span>
            </span>
          )}
        </DialogTitle>
        <DialogDescription>
          {editing ? "Vertragsdaten anpassen — Mietvertragsnummer bleibt unverändert." : "Vertrag mit Mieter, Sollstellung und Konditionen anlegen."}
        </DialogDescription>
      </DialogHeader>

      <form onSubmit={submit} className="space-y-4">
        <Section title="Vertrag">
          <div className="grid grid-cols-2 gap-3">
            <F label="Objekt">
              <SearchableCombobox
                value={propertyId}
                onChange={setPropertyId}
                options={propertiesSorted.map((p) => ({
                  id: p.id,
                  label: `${p.property_number || "—"} · ${p.name}`,
                  sublabel: [p.address?.street, p.address?.city].filter(Boolean).join(", "),
                  keywords: [p.property_number, p.name, p.address?.street, p.address?.city, p.address?.postal_code].filter(Boolean).join(" "),
                }))}
                placeholder="Objekt suchen…"
                testId="tenancy-property"
              />
            </F>
            <F label="Einheit">
              <SearchableCombobox
                value={form.unit_id}
                onChange={(v) => set("unit_id", v)}
                options={units.map((u) => ({
                  id: u.id,
                  label: `${u.unit_number} · ${u.location || ""}`.trim(),
                  sublabel: [u.unit_kind, u.rooms ? `${u.rooms} Zi.` : null, u.area ? `${u.area} m²` : null].filter(Boolean).join(" · ") || undefined,
                  keywords: [u.unit_number, u.location, u.unit_kind].filter(Boolean).join(" "),
                }))}
                placeholder={propertyId ? "Einheit suchen…" : "Zuerst Objekt wählen"}
                disabled={!propertyId}
                testId="tenancy-unit"
              />
            </F>
          </div>

          {/* Weitere Einheiten / Räume (kompakte Karten + Suche) */}
          {propertyId && form.unit_id && (units.find((x) => x.id === form.unit_id)?.linked_units || []).length > 0 && (
            <div className="mt-3 rounded border border-amber-200 bg-amber-50 px-3 py-2 text-xs text-amber-900" data-testid="tenancy-linked-units-hint">
              <div className="font-semibold mb-1">Verbundene Einheiten</div>
              <UnitLinkBadges unit={units.find((x) => x.id === form.unit_id)} units={units} />
              <div className="mt-1">„Nur gemeinsam“ verbundene Einheiten werden mitvermietet und können nicht entfernt werden, ohne die Verbindung zu lösen.</div>
            </div>
          )}
          {propertyId && form.unit_id && (
            <div className="mt-4">
              <div className="flex items-center justify-between mb-2">
                <Label className="text-xs font-semibold uppercase tracking-wider text-slate-600">
                  Weitere Einheiten / Räume
                </Label>
                <span className="text-[11px] text-slate-500">
                  {(form.additional_unit_ids || []).length > 0
                    ? `${form.additional_unit_ids.length} zusätzlich ausgewählt`
                    : "z. B. Stellplatz, Keller, Garage"}
                </span>
              </div>

              {/* Ausgewählte Zusatz-Einheiten als Chips */}
              {(form.additional_unit_ids || []).length > 0 && (
                <div className="flex flex-wrap gap-1.5 mb-2" data-testid="additional-units-chips">
                  {(form.additional_unit_ids || []).map((id) => {
                    const u = units.find((x) => x.id === id);
                    if (!u) return null;
                    return (
                      <span
                        key={id}
                        className="inline-flex items-center gap-1.5 px-2 py-1 rounded-md text-[11px] font-medium bg-indigo-50 border border-indigo-200 text-indigo-800"
                      >
                        <span className="font-mono">{u.unit_number}</span>
                        {u.location && <span className="text-indigo-700/70">· {u.location}</span>}
                        {(units.find((x) => x.id === form.unit_id)?.linked_units || []).some((l) => l.unit_id === id && l.rule === "together") ? (
                          <span className="text-amber-700" title="Nur gemeinsam vermietbar" data-testid={`additional-unit-locked-${id}`}>gekoppelt</span>
                        ) : (
                        <button
                          type="button"
                          onClick={() => set("additional_unit_ids", form.additional_unit_ids.filter((x) => x !== id))}
                          className="text-indigo-500 hover:text-rose-600"
                          aria-label="Entfernen"
                        >×</button>
                        )}
                      </span>
                    );
                  })}
                </div>
              )}

              {/* Suche + Kompakte Karten-Auswahl */}
              <AdditionalUnitsPicker
                units={units.filter((u) => u.id !== form.unit_id)}
                selectedIds={form.additional_unit_ids || []}
                editingTenancyId={editing?.id}
                onToggle={(uid) => {
                  const cur = new Set(form.additional_unit_ids || []);
                  if (cur.has(uid)) cur.delete(uid); else cur.add(uid);
                  set("additional_unit_ids", Array.from(cur));
                }}
              />

              {(form.additional_unit_ids || []).length > 0 && (
                <div className="mt-3 border-t border-slate-200 pt-3 space-y-3" data-testid="unit-grouping-section">
                  <div className="flex items-center justify-between gap-3 bg-amber-50/70 border border-amber-200 rounded-md p-3">
                    <div className="flex-1 min-w-0">
                      <div className="text-xs font-semibold text-amber-900">Darstellung der Zusatz-Einheiten</div>
                      <div className="text-[11px] text-amber-900/70 mt-0.5">
                        {form.unit_grouping === "unified"
                          ? "Einheitlich: Flächen und Zimmer werden für Dokumente/Makros addiert. Die Miete gilt EINHEITLICH für das ganze Mietverhältnis (keine Aufsummierung — Betrag oben ist der Gesamtbetrag)."
                          : 'Annex (Standard): Zusatz-Einheiten erscheinen nur als "+ ID" hinter der Haupt-Einheit.'}
                      </div>
                    </div>
                    <div className="flex items-center gap-1 bg-white rounded-md border border-amber-200 p-0.5 shrink-0">
                      <button
                        type="button"
                        onClick={() => set("unit_grouping", "annex")}
                        className={`px-2.5 py-1 text-[11px] rounded transition ${form.unit_grouping !== "unified" ? "bg-slate-900 text-white" : "text-slate-600 hover:bg-slate-50"}`}
                        data-testid="grouping-annex"
                      >Annex</button>
                      <button
                        type="button"
                        onClick={() => set("unit_grouping", "unified")}
                        className={`px-2.5 py-1 text-[11px] rounded transition ${form.unit_grouping === "unified" ? "bg-amber-600 text-white" : "text-slate-600 hover:bg-slate-50"}`}
                        data-testid="grouping-unified"
                      >Einheitlich</button>
                    </div>
                  </div>

                  {form.unit_grouping === "unified" && (
                    <UnifiedAreaSummary
                      mainUnit={units.find((x) => x.id === form.unit_id)}
                      additionalUnits={(form.additional_unit_ids || []).map((id) => units.find((u) => u.id === id)).filter(Boolean)}
                    />
                  )}
                </div>
              )}
              <p className="text-[10px] text-slate-500 mt-1.5">
                Hinweis: Die Sollstellung läuft immer am Hauptvertrag. Bei &quot;Einheitlich&quot; werden nur Flächen und Zimmer für Dokumente/Makros addiert — die Miete oben ist die Gesamtmiete für das MV.
              </p>
            </div>
          )}
          <F label="Vertragsart" className="mt-3">
            <Select value={form.tenancy_type} onValueChange={(v) => set("tenancy_type", v)}>
              <SelectTrigger data-testid="tenancy-type"><SelectValue /></SelectTrigger>
              <SelectContent>
                {Object.entries(TENANCY_TYPES).map(([k, v]) => <SelectItem key={k} value={k}>{v}</SelectItem>)}
              </SelectContent>
            </Select>
          </F>
          {ownerContact && (
            <div className="mt-3 bg-stone-50 border border-stone-200 rounded-sm p-2.5 text-xs flex items-center gap-2">
              <Info size={14} className="text-slate-500" />
              <div>Eigentümer: <span className="font-mono">[{ownerContact.owner_number || "—"}]</span> {contactDisplayName(ownerContact)}</div>
            </div>
          )}
        </Section>

        <Section title="Mieter">
          <div className="flex items-center gap-2 mb-3">
            <div className="relative flex-1">
              <MagnifyingGlass size={14} className="absolute left-2.5 top-1/2 -translate-y-1/2 text-slate-400" />
              <Input
                value={tenantSearch}
                onChange={(e) => setTenantSearch(e.target.value)}
                placeholder="Mieter suchen (Name, E-Mail, Telefon, PLZ, Ort)…"
                className="pl-8 h-9"
                data-testid="tenant-search"
              />
            </div>
            <Dialog open={tenantDialogOpen} onOpenChange={setTenantDialogOpen}>
              <DialogTrigger asChild>
                <Button type="button" variant="outline" size="sm" className="h-9 gap-1 shrink-0" data-testid="new-tenant-btn">
                  <UserPlus size={14} /> Neuer Mieter
                </Button>
              </DialogTrigger>
              <TenantQuickDialog onSaved={async (newTenant) => {
                setTenantDialogOpen(false);
                await reloadContacts?.();
                set("tenant_contact_ids", [...(form.tenant_contact_ids || []), newTenant.id]);
              }} />
            </Dialog>
          </div>

          {selectedTenants.length > 0 && (
            <div className="space-y-2 mb-3">
              <div className="text-[11px] uppercase tracking-wider text-slate-500 font-medium">Ausgewählte Mieter</div>
              {selectedTenants.map((c) => (
                <div key={c.id} className="border border-slate-300 bg-white rounded-md p-3 flex items-start justify-between gap-3" data-testid={`tenant-selected-${c.id}`}>
                  <div className="min-w-0 flex-1">
                    <div className="font-medium text-slate-900 truncate">{contactDisplayName(c)}</div>
                    <div className="mt-1 grid grid-cols-1 md:grid-cols-2 gap-x-4 gap-y-0.5 text-xs text-slate-600">
                      {(c.emails || []).length > 0 && <div className="flex items-center gap-1.5 truncate"><EnvelopeSimple size={12} className="text-slate-400 shrink-0" />{firstEmail(c.emails)}</div>}
                      {(c.phones || []).length > 0 && <div className="flex items-center gap-1.5 truncate"><Phone size={12} className="text-slate-400 shrink-0" />{firstPhone(c.phones)}</div>}
                      {(c.address?.street || c.address?.city) && (
                        <div className="flex items-center gap-1.5 truncate md:col-span-2">
                          <MapPin size={12} className="text-slate-400 shrink-0" />
                          {[c.address?.street, c.address?.house_number].filter(Boolean).join(" ")}{c.address?.zip || c.address?.city ? `, ${[c.address?.zip, c.address?.city].filter(Boolean).join(" ")}` : ""}
                        </div>
                      )}
                      {c.birth_date && <div className="text-slate-500">geb. {formatDate(c.birth_date)}</div>}
                      {c.iban && <div className="text-slate-500 font-mono truncate">IBAN: {c.iban}</div>}
                    </div>
                  </div>
                  <button type="button"
                    onClick={() => set("tenant_contact_ids", form.tenant_contact_ids.filter((x) => x !== c.id))}
                    className="text-slate-400 hover:text-rose-600 text-xs shrink-0"
                    data-testid={`tenant-remove-${c.id}`}
                  >
                    Entfernen
                  </button>
                </div>
              ))}
            </div>
          )}

          <div className="border border-stone-200 rounded-md bg-white max-h-44 overflow-y-auto" data-testid="tenant-results">
            {!tenantSearch.trim() ? (
              <div className="text-xs text-slate-400 italic p-3 text-center">
                Beginne zu tippen, um Mieter zu suchen — oder lege rechts oben einen neuen Mieter an.
              </div>
            ) : filteredContacts.length === 0 ? (
              <div className="text-xs text-slate-400 italic p-3 text-center">
                Keine Treffer für „{tenantSearch.trim()}".
              </div>
            ) : (
              filteredContacts.map((c) => {
                const active = (form.tenant_contact_ids || []).includes(c.id);
                return (
                  <button type="button" key={c.id}
                    onClick={() => set("tenant_contact_ids", active
                      ? form.tenant_contact_ids.filter((x) => x !== c.id)
                      : [...(form.tenant_contact_ids || []), c.id])}
                    className={`w-full text-left px-3 py-2 border-b border-stone-100 last:border-b-0 hover:bg-stone-50 flex items-center justify-between gap-2 ${
                      active ? "bg-emerald-50/60" : ""
                    }`}
                    data-testid={`tenancy-tenant-${c.id}`}
                  >
                    <div className="min-w-0">
                      <div className="text-sm text-slate-800 truncate">{contactDisplayName(c)}</div>
                      <div className="text-[11px] text-slate-500 truncate">
                        {firstEmail(c.emails)}{firstEmail(c.emails) && firstPhone(c.phones) ? " · " : ""}{firstPhone(c.phones)}
                      </div>
                    </div>
                    <span className={`text-[10px] uppercase tracking-wider shrink-0 ${active ? "text-emerald-700 font-semibold" : "text-slate-400"}`}>
                      {active ? "ausgewählt" : "auswählen"}
                    </span>
                  </button>
                );
              })
            )}
          </div>
        </Section>

        <Section title="Vertragsdauer">
          {(() => {
            // iter159s — Banner nur wenn wirklich blockiert oder wenn ein
            // historisches MV im gewählten Vertragszeitraum überlappt.
            // Kein Banner mehr bei „Vertragsbeginn liegt vor heute" ohne
            // echten Konflikt (User-Bug: Einheit ist LEER, nicht „belegt").
            if (!availability) return null;
            const blockers = availability.blocking_tenancies || [];
            if (blockers.length === 0) return null;
            const today = new Date().toISOString().slice(0, 10);
            // Blocker in "aktuell aktiv" vs. "in Vergangenheit beendet" unterteilen
            const activeBlockers = blockers.filter((b) => {
              const end = b.effective_end || b.contract_end;
              return !end || end >= today;
            });
            const pastBlockers = blockers.filter((b) => {
              const end = b.effective_end || b.contract_end;
              return end && end < today;
            });
            const cs = form.contract_start;
            // Überlappt der geplante Vertragsbeginn mit einem Blocker?
            const overlaps = cs ? blockers.some((b) => {
              const bStart = b.contract_start;
              const bEnd = b.effective_end || b.contract_end || "9999-12-31";
              return bStart && cs <= bEnd && bStart <= (form.contract_end || "9999-12-31");
            }) : false;
            // Aktiv belegt heute?
            if (activeBlockers.length > 0) {
              return (
                <div className="mb-3 rounded-md border border-rose-300 bg-rose-50 px-3 py-2 text-xs text-rose-900" data-testid="availability-warning">
                  <div className="font-medium flex items-center gap-1.5">
                    <span className="inline-block w-1.5 h-1.5 rounded-full bg-rose-500" />
                    Belegung erkannt — Einheit ist aktuell vermietet
                  </div>
                  {availability.earliest_available_date ? (
                    <div className="mt-0.5">
                      Frühester freier Vertragsbeginn: <b className="tabular-nums">{formatDate(availability.earliest_available_date)}</b>
                      {" "}
                      <button type="button" className="underline decoration-dotted hover:no-underline"
                              onClick={() => set("contract_start", availability.earliest_available_date)}>übernehmen</button>
                    </div>
                  ) : (
                    <div className="mt-0.5">Belegt durch unbefristetes Mietverhältnis — bitte zuerst kündigen.</div>
                  )}
                  <ul className="mt-1 pl-4 list-disc">
                    {activeBlockers.map((b) => (
                      <li key={b.id}>
                        MV <span className="tabular-nums">{b.contract_number || b.id.slice(0, 8)}</span>
                        {" "}({formatDate(b.contract_start)} – {b.effective_end ? formatDate(b.effective_end) : "unbefristet"})
                        {b.termination_status === "cancelled" && <span className="ml-1 text-amber-700">· gekündigt</span>}
                      </li>
                    ))}
                  </ul>
                </div>
              );
            }
            // Nur historische MVs vorhanden; Einheit ist HEUTE leerstehend.
            // Warnung nur wenn geplanter Vertragsbeginn tatsächlich mit einem
            // Vorgänger-MV überlappt (Rückdatierung), sonst kein Banner.
            if (overlaps) {
              return (
                <div className="mb-3 rounded-md border border-sky-300 bg-sky-50 px-3 py-2 text-xs text-sky-900" data-testid="availability-info">
                  <div className="font-medium flex items-center gap-1.5">
                    <span className="inline-block w-1.5 h-1.5 rounded-full bg-sky-500" />
                    Einheit steht derzeit leer
                  </div>
                  <div className="mt-0.5">
                    Der gewählte Vertragsbeginn überschneidet sich mit einem beendeten Mietverhältnis. Bitte prüfen, ob die Rückdatierung wirklich beabsichtigt ist.
                  </div>
                  <ul className="mt-1 pl-4 list-disc">
                    {pastBlockers.map((b) => (
                      <li key={b.id}>
                        MV <span className="tabular-nums">{b.contract_number || b.id.slice(0, 8)}</span>
                        {" "}({formatDate(b.contract_start)} – {b.effective_end ? formatDate(b.effective_end) : "unbefristet"})
                        <span className="ml-1 text-slate-500">· beendet</span>
                      </li>
                    ))}
                  </ul>
                </div>
              );
            }
            // Sonst: Einheit ist frei und geplanter Vertrag konfliktfrei → kein Banner.
            return null;
          })()}
          <div className="grid grid-cols-3 gap-3">
            <F label="Vertragsbeginn"><Input type="date" required value={form.contract_start} onChange={(e) => set("contract_start", e.target.value)} data-testid="contract-start" /></F>
            <F label="Vertragsende">
              <Input type="date" lang="de-DE" value={form.contract_end} onChange={(e) => set("contract_end", e.target.value)} disabled={openEnd} data-testid="contract-end" />
            </F>
            <div className="flex items-end pb-1">
              <label className="inline-flex items-center gap-2 text-sm cursor-pointer select-none">
                <input type="checkbox" className="h-4 w-4 accent-slate-900" checked={openEnd} onChange={(e) => setOpenEnd(e.target.checked)} data-testid="open-end-checkbox" />
                <span className="text-slate-700">Unbefristet</span>
              </label>
            </div>
            <F label="Kündigungsfrist (Monate)"><Input type="number" min="0" max="12" value={form.termination_notice_months} onChange={(e) => set("termination_notice_months", e.target.value)} /></F>
            <div className="col-span-3 grid grid-cols-1 sm:grid-cols-2 gap-3" data-testid="handover-return-row">
              <F label="Übergabe (Einzug) · Datum & Uhrzeit">
                <div className="grid grid-cols-[minmax(0,1fr)_7rem] gap-2">
                  <Input type="date" lang="de-DE" value={form.handover_date} onChange={(e) => set("handover_date", e.target.value)} data-testid="handover-date" />
                  <Input type="time" lang="de-DE" step={300} className="min-w-0" value={form.handover_time} onChange={(e) => set("handover_time", e.target.value)} disabled={!form.handover_date} data-testid="handover-time" />
                </div>
                <Input className="mt-2" placeholder="Treffpunkt (leer = vor der Wohnungstür)" value={form.handover_location || ""} onChange={(e) => set("handover_location", e.target.value)} disabled={!form.handover_date} data-testid="handover-location" />
              </F>
              <F label="Rücknahme (Auszug) · Datum & Uhrzeit">
                <div className="grid grid-cols-[minmax(0,1fr)_7rem] gap-2">
                  <Input type="date" lang="de-DE" value={form.return_date} onChange={(e) => set("return_date", e.target.value)} data-testid="return-date" />
                  <Input type="time" lang="de-DE" step={300} className="min-w-0" value={form.return_time} onChange={(e) => set("return_time", e.target.value)} disabled={!form.return_date} data-testid="return-time" />
                </div>
                <Input className="mt-2" placeholder="Treffpunkt (leer = vor der Wohnungstür)" value={form.return_location || ""} onChange={(e) => set("return_location", e.target.value)} disabled={!form.return_date} data-testid="return-location" />
              </F>
            </div>
            <F label="Personen im Haushalt"><Input type="number" min="0" value={form.persons_count} onChange={(e) => set("persons_count", e.target.value)} /></F>
            {/* 2026-08 · Anteilige-Miete-Option nur sichtbar, wenn Vertragsbeginn NICHT der 1. ist */}
            {form.contract_start && new Date(form.contract_start).getDate() !== 1 && (
              <div className="col-span-3 -mt-1 rounded-md border border-slate-200 bg-slate-50 px-3 py-2" data-testid="prorate-first-month">
                <label className="inline-flex items-start gap-2 text-sm cursor-pointer select-none">
                  <input
                    type="checkbox"
                    className="h-4 w-4 mt-0.5 accent-emerald-600"
                    checked={!!form.prorate_first_month}
                    onChange={(e) => set("prorate_first_month", e.target.checked)}
                    data-testid="prorate-first-month-checkbox"
                  />
                  <span>
                    <span className="font-medium text-slate-900">Miete des Startmonats anteilig berechnen</span>
                    <span className="block text-[11px] text-slate-500 mt-0.5">
                      Vertragsbeginn ist der {new Date(form.contract_start).getDate()}. des Monats.
                      Bei aktivierter Option wird für den Startmonat nur der anteilige Betrag
                      ({(() => {
                        const d = new Date(form.contract_start);
                        const daysInM = new Date(d.getFullYear(), d.getMonth() + 1, 0).getDate();
                        const rem = daysInM - d.getDate() + 1;
                        return `${rem}/${daysInM} Tage`;
                      })()}) gebucht. Ohne Häkchen wird der Startmonat übersprungen und
                      erst der Folgemonat regulär belastet.
                    </span>
                  </span>
                </label>
                {/* 2026-02 (feb) — Abweichende erste Miete: individuelle Beträge */}
                <div className="mt-2 pt-2 border-t border-slate-200">
                  <label className="inline-flex items-start gap-2 text-sm cursor-pointer select-none">
                    <input
                      type="checkbox"
                      className="h-4 w-4 mt-0.5 accent-emerald-600"
                      checked={!!form.first_month_custom}
                      onChange={(e) => set("first_month_custom", e.target.checked
                        ? { rent_cold: form.rent_cold || 0, utilities_advance: form.utilities_advance || 0, heating_advance: form.heating_advance || 0, parking_rent: form.parking_rent || 0 }
                        : null)}
                      data-testid="first-month-custom-checkbox"
                    />
                    <span>
                      <span className="font-medium text-slate-900">Abweichende erste Miete eingeben</span>
                      <span className="block text-[11px] text-slate-500 mt-0.5">
                        Trägst du hier eigene Beträge ein, wird die automatische anteilige Berechnung
                        für den Startmonat überschrieben. Nur Kategorien mit einem Wert werden gebucht.
                      </span>
                    </span>
                  </label>
                  {form.first_month_custom && (
                    <div className="mt-2 grid grid-cols-2 gap-2 pl-6">
                      <div>
                        <label className="block text-[10px] uppercase tracking-wider text-slate-500 mb-0.5">Kaltmiete (€)</label>
                        <Input type="number" step="0.01" min="0"
                          value={form.first_month_custom.rent_cold ?? ""}
                          onChange={(e) => set("first_month_custom", { ...form.first_month_custom, rent_cold: e.target.value === "" ? null : Number(e.target.value) })}
                          data-testid="first-month-rent-cold" />
                      </div>
                      <div>
                        <label className="block text-[10px] uppercase tracking-wider text-slate-500 mb-0.5">NK-Vorauszahlung (€)</label>
                        <Input type="number" step="0.01" min="0"
                          value={form.first_month_custom.utilities_advance ?? ""}
                          onChange={(e) => set("first_month_custom", { ...form.first_month_custom, utilities_advance: e.target.value === "" ? null : Number(e.target.value) })}
                          data-testid="first-month-utilities" />
                      </div>
                      <div>
                        <label className="block text-[10px] uppercase tracking-wider text-slate-500 mb-0.5">Heizung-Vorauszahlung (€)</label>
                        <Input type="number" step="0.01" min="0"
                          value={form.first_month_custom.heating_advance ?? ""}
                          onChange={(e) => set("first_month_custom", { ...form.first_month_custom, heating_advance: e.target.value === "" ? null : Number(e.target.value) })}
                          data-testid="first-month-heating" />
                      </div>
                      <div>
                        <label className="block text-[10px] uppercase tracking-wider text-slate-500 mb-0.5">Stellplatz/Garage (€)</label>
                        <Input type="number" step="0.01" min="0"
                          value={form.first_month_custom.parking_rent ?? ""}
                          onChange={(e) => set("first_month_custom", { ...form.first_month_custom, parking_rent: e.target.value === "" ? null : Number(e.target.value) })}
                          data-testid="first-month-parking" />
                      </div>
                    </div>
                  )}
                </div>
              </div>
            )}
            {/* Sprint 5g / 5g-ext — Mietkonto-Auswahl aus Bankkonten aller Eigentümer */}
            <F label="Mietkonto (Eigentümer-Bankverbindung)" className="col-span-2">
              {ownerBankAccounts.length === 0 ? (
                <div className="text-[11px] text-amber-700 bg-amber-50 border border-amber-200 rounded px-2 py-1.5" data-testid="rent-account-empty-hint">
                  ⚠ Der/die Eigentümer hat/haben noch keine Bankverbindung hinterlegt. Bitte im Kontakt „Bankverbindungen" ergänzen — dann steht sie hier zur Auswahl.
                </div>
              ) : (
                <>
                  <Select
                    value={form.rent_account_id || ""}
                    onValueChange={(v) => set("rent_account_id", v)}
                  >
                    <SelectTrigger data-testid="rent-account-select">
                      <SelectValue placeholder="Mietkonto wählen…" />
                    </SelectTrigger>
                    <SelectContent>
                      {ownerBankAccounts.map((a) => (
                        <SelectItem key={a.id} value={a.id} data-testid={`rent-account-opt-${a.id}`}>
                          <span className="text-slate-500 text-[11px]">{a.owner_label || "Eigentümer"}</span>
                          <span className="mx-1 text-slate-300">·</span>
                          <span className="font-medium">{a.label || (a.bank_name || "Konto")}</span>
                          <span className="text-slate-500 ml-2 font-mono text-[11px]">
                            {(a.iban || "").replace(/(.{4})/g, "$1 ").trim()}
                          </span>
                          {a.is_default && <span className="ml-1.5 text-[10px] text-emerald-700">·Standard</span>}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                  {!form.rent_account_id && (
                    <div
                      className="mt-1.5 text-[11px] text-amber-700 bg-amber-50 border border-amber-200 rounded px-2 py-1.5"
                      data-testid="rent-account-unset-hint"
                    >
                      ⚠ Kein Mietkonto ausgewählt — bei Briefen an den Mieter wird die Bankverbindung der Hausverwaltung (Fallback aus den Einstellungen) verwendet. Bitte ein Eigentümer-Konto wählen, damit Mietzahlungen korrekt zugeordnet werden.
                    </div>
                  )}
                </>
              )}
            </F>
            {/* Sprint 5e — Im Mietvertrag abweichende Wohnungsgröße */}
            <F label="Wohnfl. lt. Mietvertrag (m²)">
              <Input
                type="number" min="0" step="0.01"
                value={form.contract_living_area}
                onChange={(e) => set("contract_living_area", e.target.value)}
                placeholder={selectedUnit?.living_area ? String(selectedUnit.living_area) : "z. B. 67,47"}
                data-testid="contract-living-area"
              />
              <div className="text-[11px] text-slate-500 mt-1 leading-snug">
                {selectedUnit?.living_area && (
                  <span className="inline-block bg-slate-100 border border-slate-200 rounded-sm px-1.5 py-0.5 mr-1 tabular-nums text-slate-700">
                    Einheit: {selectedUnit.living_area} m²
                  </span>
                )}
                Optional — nur ausfüllen, wenn die im Mietvertrag genannte Fläche
                von der hinterlegten Wohnfläche abweicht.
              </div>
            </F>
          </div>

          {/* Sprint 5a — Kündigungsverzicht auf ordentliches Kündigungsrecht */}
          <label className="flex items-start gap-2 mt-3 text-sm cursor-pointer" data-testid="ordinary-waiver-row">
            <input
              type="checkbox" className="mt-0.5 h-4 w-4 accent-slate-900"
              checked={!!form.ordinary_termination_waiver}
              onChange={(e) => set("ordinary_termination_waiver", e.target.checked)}
              data-testid="ordinary-waiver-checkbox"
            />
            <span>
              <span className="text-slate-800 font-medium">Kündigungsverzicht auf ordentliches Kündigungsrecht</span>
              <span className="block text-[11px] text-slate-600 mt-0.5">
                Beide Parteien verzichten — in der Regel für die Mindestvertragslaufzeit — auf das ordentliche Kündigungsrecht.
                Außerordentliche Kündigung aus wichtigem Grund bleibt davon unberührt.
              </span>
            </span>
          </label>

          {/* Phase 21β-1.1 – Mindestlaufzeit + automatische Verlängerung */}
          <div className="mt-3 grid grid-cols-1 md:grid-cols-2 gap-3 bg-slate-50 border border-slate-200 rounded-md p-3" data-testid="contract-term-extras">
            <div>
              <Label className="text-xs font-medium text-slate-700">Mindestvertragslaufzeit (Monate)</Label>
              <Input
                type="number" min="0" max="120"
                placeholder="z. B. 24 für 2 Jahre"
                value={form.minimum_term_months ?? ""}
                onChange={(e) => set("minimum_term_months", e.target.value)}
                data-testid="minimum-term-months"
              />
              <div className="text-[11px] text-slate-500 mt-1">
                Vor Ablauf ist eine Kündigung nicht möglich. Leer = keine Mindestlaufzeit.
              </div>
            </div>
            <div>
              <Label className="text-xs font-medium text-slate-700">Automatische Verlängerung (Monate)</Label>
              <Input
                type="number" min="0" max="120"
                placeholder="z. B. 12 für jährlich"
                value={form.auto_renew_months ?? ""}
                onChange={(e) => set("auto_renew_months", e.target.value)}
                data-testid="auto-renew-months"
              />
              <div className="text-[11px] text-slate-500 mt-1">
                Verlängert sich automatisch um diese Anzahl Monate, wenn nicht rechtzeitig gekündigt wird.
              </div>
            </div>
          </div>
          {form.tenancy_type === "01" && (
            <label
              className="mt-3 flex items-start gap-2 text-sm cursor-pointer select-none rounded-md border border-emerald-200 bg-emerald-50/60 p-2.5"
              data-testid="update-tenant-address-row"
            >
              <input
                type="checkbox"
                className="mt-0.5 h-4 w-4 accent-emerald-700"
                checked={!!form.update_tenant_address}
                onChange={(e) => set("update_tenant_address", e.target.checked)}
                data-testid="update-tenant-address-checkbox"
              />
              <span>
                <span className="text-slate-800 font-medium">Adresse der Mieter ab Einzug auf die Mietwohnung setzen</span>
                <span className="block text-[11px] text-slate-600 mt-0.5">
                  {(() => {
                    const stichtag = form.handover_date || form.contract_start;
                    const today = new Date().toISOString().slice(0, 10);
                    const isFuture = stichtag && stichtag > today;
                    if (isFuture) {
                      return <>Die Umstellung erfolgt <b>automatisch am {formatDate(stichtag)}</b> (Übergabetermin bzw. Vertragsbeginn). Bis dahin bleibt die Privatadresse unverändert; anschließend wird sie in „Ehemalige Adressen" archiviert. Nur für Wohnraummietverträge.</>;
                    }
                    return <>Die bisherigen Adressen werden mit Stichtag {stichtag ? formatDate(stichtag) : "(Vertragsbeginn)"} automatisch in „Ehemalige Adressen" archiviert. Nur für Wohnraummietverträge verfügbar.</>;
                  })()}
                </span>
              </span>
            </label>
          )}
        </Section>

        <Section title={isVatType ? "Miete & Nebenkosten (Netto)" : "Miete & Nebenkosten"}>
          {/* Sprint 5a — Gesamt-Betriebskostenvorauszahlung statt Aufschlüsselung */}
          <label className="flex items-center gap-2 mb-3 text-sm cursor-pointer" data-testid="bk-total-row">
            <input
              type="checkbox" className="h-4 w-4 accent-slate-900"
              checked={!!form.bk_total_only}
              onChange={(e) => set("bk_total_only", e.target.checked)}
              data-testid="bk-total-checkbox"
            />
            <span className="text-slate-700">
              Nur <strong>gesamte Betriebskosten-Vorauszahlung</strong> vereinbart
              <span className="text-[11px] text-slate-500 ml-2">(keine Aufschlüsselung in Neben-/Heizkosten)</span>
            </span>
          </label>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
            <F label={isVatType ? "Nettokaltmiete €" : "Kaltmiete €"}><Input type="number" min="0" step="0.01" value={form.rent_cold} onChange={(e) => set("rent_cold", e.target.value)} data-testid="tenancy-rent" /></F>
            {form.bk_total_only ? (
              <F label="Betriebskosten gesamt €" className="col-span-2">
                <Input type="number" min="0" step="0.01" value={form.utilities_total ?? ""} onChange={(e) => set("utilities_total", e.target.value)} data-testid="tenancy-bk-total" />
              </F>
            ) : (
              <>
                <F label="Nebenkosten €"><Input type="number" min="0" step="0.01" value={form.utilities_advance} onChange={(e) => set("utilities_advance", e.target.value)} /></F>
                <F label="Heizkosten €"><Input type="number" min="0" step="0.01" value={form.heating_advance} onChange={(e) => set("heating_advance", e.target.value)} /></F>
              </>
            )}
            <F label="Stellplatz €"><Input type="number" min="0" step="0.01" value={form.parking_rent} onChange={(e) => set("parking_rent", e.target.value)} /></F>
          </div>
          <SurchargesEditor value={form.surcharges || []} onChange={(v) => set("surcharges", v)} />

          {isVatType && (
            <div className="mt-3 bg-amber-50/60 border border-amber-200 rounded-sm p-3">
              <div className="flex items-center justify-between gap-3 mb-2">
                <Label className="text-xs text-amber-900 font-semibold uppercase tracking-wider">Umsatzsteuer</Label>
                <div className="flex items-center gap-2">
                  <Input
                    type="number"
                    step="0.1"
                    min="0"
                    max="100"
                    value={form.vat_rate}
                    onChange={(e) => set("vat_rate", e.target.value)}
                    className="w-24 h-8 text-right"
                    data-testid="tenancy-vat-rate"
                  />
                  <span className="text-sm text-amber-900 font-medium">%</span>
                </div>
              </div>
              <div className="text-[11px] text-amber-900/80 italic mb-2">
                {isCommercialType
                  ? "Bei gewerblichen Vermietungen (Gewerbe / Keller / Speicher / Außenstellplatz / Tiefgarage / Garage) wird automatisch 19 % USt. vorbelegt. Du kannst den Satz manuell anpassen."
                  : "Wohnraum-Mietverträge sind normalerweise umsatzsteuerfrei. Setze den Satz auf 0 % oder trage einen Wert ein, falls du zur USt. optiert hast (z. B. bei mitvermietetem Gewerbe-Stellplatz)."}
              </div>
              <div className="grid grid-cols-2 md:grid-cols-4 gap-2 text-xs">
                <BruttoCell label="Kaltmiete brutto" value={rcGross} />
                <BruttoCell label="Nebenkosten brutto" value={uaGross} />
                <BruttoCell label="Heizkosten brutto" value={haGross} />
                <BruttoCell label="Stellplatz brutto" value={prGross} />
              </div>
            </div>
          )}

          {totalRentNet > 0 && (
            <div className="mt-3 flex items-center justify-end text-sm gap-4">
              {isVatType && (
                <span className="text-slate-500">
                  Netto-Summe: <span className="font-semibold text-slate-800 tabular-nums" data-testid="net-rent-total">{formatEUR(totalRentNet)}</span>
                </span>
              )}
              <span className="text-slate-500">
                {isVatType ? "Brutto-Warmmiete" : "Warmmiete"}: <span className="font-semibold text-slate-900 tabular-nums" data-testid="warm-rent-total">{formatEUR(isVatType ? totalRentGross : totalRentNet)}</span>
              </span>
            </div>
          )}
        </Section>

        <Section title="Kaution">
          <div className="grid grid-cols-3 gap-3">
            <F label="Kaution €"><Input type="number" step="0.01" value={form.deposit} onChange={(e) => set("deposit", e.target.value)} data-testid="tenancy-deposit" /></F>
            <F label="Status">
              <Select value={form.deposit_status} onValueChange={(v) => set("deposit_status", v)}>
                <SelectTrigger><SelectValue /></SelectTrigger>
                <SelectContent>
                  {Object.entries(DEPOSIT_STATUS_LABELS).map(([k, v]) => <SelectItem key={k} value={k}>{v}</SelectItem>)}
                </SelectContent>
              </Select>
            </F>
            <F label="Kautionsart">
              <Select value={form.deposit_type || "cash"} onValueChange={(v) => set("deposit_type", v)}>
                <SelectTrigger data-testid="tenancy-deposit-type"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="cash">Barkaution (§ 551 BGB)</SelectItem>
                  <SelectItem value="buergschaft">Bürgschaft</SelectItem>
                  <SelectItem value="verpfaendung">Verpfändung Sparbuch</SelectItem>
                </SelectContent>
              </Select>
            </F>
          </div>
          {(form.deposit_type === "buergschaft" || form.deposit_type === "verpfaendung") && (
            <div className="grid grid-cols-3 gap-3 mt-3">
              <F label={form.deposit_type === "buergschaft" ? "Bürge (Bank/Versicherung)" : "Bank (verpfändetes Sparbuch)"}>
                <Input value={form.deposit_provider || ""} onChange={(e) => set("deposit_provider", e.target.value)}
                       placeholder="z. B. Sparkasse Musterstadt" data-testid="tenancy-deposit-provider" />
              </F>
              <F label="Urkunde/Nachweis erhalten?">
                <Select value={form.deposit_document_received ? "yes" : "no"}
                        onValueChange={(v) => set("deposit_document_received", v === "yes")}>
                  <SelectTrigger data-testid="tenancy-deposit-doc-received"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="no">nein</SelectItem>
                    <SelectItem value="yes">ja</SelectItem>
                  </SelectContent>
                </Select>
              </F>
            </div>
          )}
          {depositHint && (
            <div className={`mt-3 text-xs px-3 py-2 rounded-sm border flex items-start gap-2 ${
              depositHint.tone === "warning" ? "bg-amber-50 border-amber-200 text-amber-800"
              : depositHint.tone === "ok" ? "bg-emerald-50 border-emerald-200 text-emerald-800"
              : "bg-stone-50 border-stone-200 text-slate-700"
            }`} data-testid="deposit-hint">
              <Info size={14} className="mt-0.5 shrink-0" />
              <span>{depositHint.msg}</span>
            </div>
          )}
        </Section>

        <Section title="Mietart & Anpassung">
          <div className="grid grid-cols-3 gap-3">
            <F label="Mietart">
              <Select value={form.rent_type} onValueChange={(v) => set("rent_type", v)}>
                <SelectTrigger data-testid="rent-type"><SelectValue /></SelectTrigger>
                <SelectContent>
                  {Object.entries(RENT_TYPES).map(([k, v]) => <SelectItem key={k} value={k}>{v}</SelectItem>)}
                </SelectContent>
              </Select>
            </F>
            {form.rent_type === "index" && (
              <>
                <F label="Indexbasis-Jahr">
                  <Select value={form.index_base_date || "2020"} onValueChange={(v) => set("index_base_date", v)}>
                    <SelectTrigger data-testid="index-base-year"><SelectValue /></SelectTrigger>
                    <SelectContent>
                      {Object.entries(INDEX_BASES).map(([k, v]) => <SelectItem key={k} value={k}>{v}</SelectItem>)}
                    </SelectContent>
                  </Select>
                </F>
                <F label="Index-Wert zum Vertragsstart">
                  <Input
                    type="number" step="0.1"
                    value={form.index_base}
                    onChange={(e) => set("index_base", e.target.value)}
                    placeholder={vpiHit ? `${vpiHit.value}` : "z. B. 119,2"}
                    data-testid="index-base-value"
                  />
                  {vpiHit && (
                    <div className="text-[11px] text-emerald-700 mt-1" data-testid="vpi-auto-hint">
                      ✓ VPI {vpiHit.date} (Basis 2020=100): <span className="font-mono font-semibold">{vpiHit.value}</span>
                      {vpiHit.fallback && <span className="text-amber-700 ml-1">(geschätzter Fallback-Wert)</span>}
                    </div>
                  )}
                </F>
                <F label="Schwellenwert (%)">
                  <Input
                    type="text"
                    inputMode="decimal"
                    value={form.index_threshold_pct === "" ? "" : String(form.index_threshold_pct).replace(".", ",")}
                    onChange={(e) => set("index_threshold_pct", e.target.value.replace(",", "."))}
                    onBlur={(e) => {
                      const raw = (e.target.value || "").replace(",", ".").trim();
                      if (raw === "") return;
                      const n = Number(raw);
                      if (Number.isFinite(n)) set("index_threshold_pct", n.toFixed(1));
                    }}
                    placeholder="z. B. 5,0 %"
                    data-testid="index-threshold"
                  />
                </F>
              </>
            )}
          </div>
          {form.rent_type === "index" && (
            <div className="mt-3 text-xs text-slate-500 italic">
              Die automatische Berechnung der Mietanpassung erfolgt auf Basis des Verbraucherpreisindex (Statistisches Bundesamt) und dem hier gewählten Basis-Jahr.
            </div>
          )}
        </Section>

        <Section title="Zahlung">
          <div className="grid grid-cols-3 gap-3">
            <F label="Zahlrhythmus">
              <Select value={form.payment_rhythm} onValueChange={(v) => set("payment_rhythm", v)}>
                <SelectTrigger><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="monthly">Monatlich</SelectItem>
                  <SelectItem value="quarterly">Quartalsweise</SelectItem>
                  <SelectItem value="yearly">Jährlich</SelectItem>
                </SelectContent>
              </Select>
            </F>
            <F label={form.due_day_mode === "calendar" ? "Fälligkeit (Kalendertag)" : "Fälligkeit (n-ter Werktag)"}>
              <Input type="number" min="1" max="28" value={form.due_day} onChange={(e) => set("due_day", e.target.value)} data-testid="due-day" />
            </F>
            <F label="Modus">
              <Select value={form.due_day_mode || "workday"} onValueChange={(v) => set("due_day_mode", v)}>
                <SelectTrigger data-testid="due-day-mode"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="workday">Werktag (Mo–Fr, ohne Feiertag)</SelectItem>
                  <SelectItem value="calendar">Kalendertag (fixer Tag)</SelectItem>
                </SelectContent>
              </Select>
            </F>
          </div>
          <div className="grid grid-cols-3 gap-3 mt-3">
            {form.due_day_mode !== "calendar" && (
              <F label="Bundesland (für Feiertage)">
                <Select value={form.due_day_region || "NW"} onValueChange={(v) => set("due_day_region", v)}>
                  <SelectTrigger data-testid="due-day-region"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="BW">Baden-Württemberg</SelectItem>
                    <SelectItem value="BY">Bayern</SelectItem>
                    <SelectItem value="BE">Berlin</SelectItem>
                    <SelectItem value="BB">Brandenburg</SelectItem>
                    <SelectItem value="HB">Bremen</SelectItem>
                    <SelectItem value="HH">Hamburg</SelectItem>
                    <SelectItem value="HE">Hessen</SelectItem>
                    <SelectItem value="MV">Mecklenburg-Vorpommern</SelectItem>
                    <SelectItem value="NI">Niedersachsen</SelectItem>
                    <SelectItem value="NW">Nordrhein-Westfalen</SelectItem>
                    <SelectItem value="RP">Rheinland-Pfalz</SelectItem>
                    <SelectItem value="SL">Saarland</SelectItem>
                    <SelectItem value="SN">Sachsen</SelectItem>
                    <SelectItem value="ST">Sachsen-Anhalt</SelectItem>
                    <SelectItem value="SH">Schleswig-Holstein</SelectItem>
                    <SelectItem value="TH">Thüringen</SelectItem>
                  </SelectContent>
                </Select>
              </F>
            )}
            <F label="SEPA-Mandat">
              <Select value={form.sepa_mandate ? "yes" : "no"} onValueChange={(v) => set("sepa_mandate", v === "yes")}>
                <SelectTrigger><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="no">Nein</SelectItem>
                  <SelectItem value="yes">Ja</SelectItem>
                </SelectContent>
              </Select>
            </F>
          </div>
          <div className="mt-2 text-[11px] text-slate-500 italic">
            {form.due_day_mode === "calendar"
              ? "Kalendertag: die Fälligkeit fällt exakt auf diesen Tag jedes Monats."
              : "Werktag (BGH VIII ZR 129/09): Mo–Fr ohne Feiertage. Ein Samstag zählt nicht als Werktag."}
          </div>
        </Section>

        <F label="Notizen / Vertragsklauseln">
          <Textarea rows={2} value={form.notes} onChange={(e) => set("notes", e.target.value)} placeholder="z. B. Tierhaltung erlaubt …" />
        </F>

        {/* Sprint 5a — Auto-Sollstellungen-Toggle */}
        <label className="flex items-start gap-2 bg-emerald-50/50 border border-emerald-200 rounded-md p-3 text-sm cursor-pointer" data-testid="auto-postings-row">
          <input
            type="checkbox" className="mt-0.5 h-4 w-4 accent-emerald-700"
            checked={form.auto_create_postings !== false}
            onChange={(e) => set("auto_create_postings", e.target.checked)}
            data-testid="auto-postings-checkbox"
          />
          <span>
            <span className="text-slate-800 font-medium">Sollstellungen automatisch erstellen</span>
            <span className="block text-[11px] text-slate-600 mt-0.5">
              Beim Anlegen werden Sollstellungen für Kaltmiete, Nebenkosten, Heizkosten und Stellplatz ab Vertragsbeginn automatisch erzeugt. Deaktivieren, wenn du das später manuell verbuchen möchtest.
            </span>
          </span>
        </label>

        <label className="flex items-start gap-2 bg-slate-50 border border-slate-200 rounded-md p-3 text-sm" data-testid="surplus-mode-row">
          <span className="flex-1">
            <span className="text-slate-800 font-medium">Guthaben-Verrechnung</span>
            <span className="block text-[11px] text-slate-600 mt-0.5">
              Manuell: Guthaben wird erst auf Klick mit fälligen offenen Posten verrechnet. Automatisch: sofort, mit Mitteilung an den Mieter.
            </span>
          </span>
          <select value={form.surplus_offset_mode || "manual"} onChange={(e) => set("surplus_offset_mode", e.target.value)}
                  className="h-9 border border-slate-300 rounded-md px-2 text-sm bg-white" data-testid="surplus-mode-select">
            <option value="manual">Manuell (Standard)</option>
            <option value="auto">Automatisch</option>
          </select>
        </label>

        <DialogFooter>
          <Button type="submit" className="bg-slate-900 hover:bg-slate-800" data-testid="tenancy-save-btn">{editing ? "Aktualisieren" : "Mietverhältnis anlegen"}</Button>
        </DialogFooter>
      </form>
    </DialogContent>
  );
}

function TenantQuickDialog({ onSaved }) {
  const [form, setForm] = useState({
    contact_type: "person",
    salutation: "",
    first_name: "", last_name: "", company_name: "", contact_person: "",
    birth_date: "",
    address: { street: "", house_number: "", zip: "", city: "", country: "DE" },
    email: "", phone: "",
    iban: "", bic: "", tax_id: "",
    notes: "",
  });
  const set = (k, v) => setForm((s) => ({ ...s, [k]: v }));
  const setAddr = (k, v) => setForm((s) => ({ ...s, address: { ...s.address, [k]: v } }));
  // Sprint 5c — PLZ→Ort Auto-Fill
  const { hit: plzHit } = usePlzAutocomplete(
    form.address?.zip,
    (city) => setAddr("city", city),
    form.address?.city || "",
  );

  const submit = async (e) => {
    e.preventDefault();
    e.stopPropagation();
    const payload = {
      contact_type: form.contact_type,
      salutation: form.contact_type === "person" ? (form.salutation || null) : null,
      first_name: form.contact_type === "person" ? (form.first_name || null) : null,
      last_name: form.contact_type === "person" ? (form.last_name || null) : null,
      company_name: form.contact_type !== "person" ? (form.company_name || null) : null,
      contact_person: form.contact_person || null,
      birth_date: form.birth_date || null,
      address: form.address,
      emails: form.email ? [form.email] : [],
      phones: form.phone ? [form.phone] : [],
      iban: form.iban || null,
      bic: form.bic || null,
      tax_id: form.tax_id || null,
      notes: form.notes || null,
      roles: ["tenant"],
    };
    try {
      const { data } = await api.post("/contacts", payload);
      toast.success("Mieter angelegt");
      onSaved(data);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Fehler beim Anlegen");
    }
  };

  return (
    <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
      <DialogHeader><DialogTitle>Neuer Mieter</DialogTitle></DialogHeader>
      <form onSubmit={submit} className="space-y-3">
        <Section title="Stammdaten">
          <div className="grid grid-cols-2 gap-3">
            <F label="Typ">
              <Select value={form.contact_type} onValueChange={(v) => set("contact_type", v)}>
                <SelectTrigger data-testid="tenant-type"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="person">Person</SelectItem>
                  <SelectItem value="company">Firma</SelectItem>
                  <SelectItem value="institution">Institution</SelectItem>
                </SelectContent>
              </Select>
            </F>
            <F label="Geburtsdatum (optional)">
              <Input type="date" value={form.birth_date} onChange={(e) => set("birth_date", e.target.value)} />
            </F>
          </div>
          {form.contact_type === "person" ? (
            <div className="grid grid-cols-3 gap-3 mt-3">
              <F label="Anrede">
                <Select value={form.salutation || ""} onValueChange={(v) => set("salutation", v)}>
                  <SelectTrigger data-testid="tenant-salutation"><SelectValue placeholder="—" /></SelectTrigger>
                  <SelectContent>
                    {Object.entries(SALUTATIONS).map(([k, v]) => (
                      <SelectItem key={k} value={k}>{v}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </F>
              <F label="Vorname"><Input value={form.first_name} onChange={(e) => set("first_name", e.target.value)} data-testid="tenant-first-name" /></F>
              <F label="Nachname"><Input required value={form.last_name} onChange={(e) => set("last_name", e.target.value)} data-testid="tenant-last-name" /></F>
            </div>
          ) : (
            <div className="grid grid-cols-2 gap-3 mt-3">
              <F label="Firmenname"><Input required value={form.company_name} onChange={(e) => set("company_name", e.target.value)} /></F>
              <F label="Ansprechpartner"><Input value={form.contact_person} onChange={(e) => set("contact_person", e.target.value)} /></F>
            </div>
          )}
        </Section>
        <Section title="Adresse">
          <div className="grid grid-cols-4 gap-3">
            <F label="Straße" className="col-span-2"><Input value={form.address.street} onChange={(e) => setAddr("street", e.target.value)} /></F>
            <F label="Nr."><Input value={form.address.house_number} onChange={(e) => setAddr("house_number", e.target.value)} /></F>
            <F label="PLZ"><Input value={form.address.zip} onChange={(e) => setAddr("zip", e.target.value)} maxLength={5} placeholder="5-stellig" data-testid="tenant-zip" /></F>
            <F label="Ort" className="col-span-3"><Input value={form.address.city} onChange={(e) => setAddr("city", e.target.value)} data-testid="tenant-city" /></F>
          </div>
          <PlzAutocompleteHint hit={plzHit} />
        </Section>
        <Section title="Kontakt">
          <div className="grid grid-cols-2 gap-3">
            <F label="E-Mail"><Input type="email" value={form.email} onChange={(e) => set("email", e.target.value)} /></F>
            <F label="Telefon"><Input value={form.phone} onChange={(e) => set("phone", e.target.value)} /></F>
          </div>
        </Section>
        <Section title="Bank & Steuer">
          <div className="grid grid-cols-3 gap-3">
            <F label="IBAN" className="col-span-2"><Input value={form.iban} onChange={(e) => set("iban", e.target.value)} /></F>
            <F label="BIC"><Input value={form.bic} onChange={(e) => set("bic", e.target.value)} /></F>
            <F label="Steuer-ID" className="col-span-3"><Input value={form.tax_id} onChange={(e) => set("tax_id", e.target.value)} /></F>
          </div>
        </Section>
        <F label="Notizen"><Textarea rows={2} value={form.notes} onChange={(e) => set("notes", e.target.value)} /></F>
        <DialogFooter>
          <Button type="submit" className="bg-slate-900 hover:bg-slate-800" data-testid="tenant-save-btn">Mieter anlegen</Button>
        </DialogFooter>
      </form>
    </DialogContent>
  );
}

function Section({ title, children }) {
  return (
    <div className="border border-stone-200 rounded-md p-3 bg-stone-50/40">
      <div className="text-[11px] uppercase tracking-wider text-slate-600 font-semibold mb-3">{title}</div>
      {children}
    </div>
  );
}

function BruttoCell({ label, value }) {
  return (
    <div className="bg-white border border-amber-200 rounded-sm px-2 py-1.5">
      <div className="text-[10px] uppercase tracking-wider text-amber-800/70">{label}</div>
      <div className="text-sm font-semibold text-slate-900 tabular-nums">{formatEUR(value)}</div>
    </div>
  );
}


function F({ label, children, className = "" }) {
  return (
    <div className={className}>
      <Label className="text-xs text-slate-700">{label}</Label>
      <div className="mt-1">{children}</div>
    </div>
  );
}


// Phase 21β-1.1: Kompakter Picker mit Suche für Zusatz-Einheiten
function AdditionalUnitsPicker({ units, selectedIds, editingTenancyId, onToggle }) {
  const [q, setQ] = useState("");
  const [expanded, setExpanded] = useState(false);
  const query = q.trim().toLowerCase();
  const matches = useMemo(() => {
    if (!query) return units;
    return units.filter((u) => {
      const hay = `${u.unit_number || ""} ${u.location || ""} ${u.kfz_code || ""} ${u.unit_type || ""}`.toLowerCase();
      return hay.includes(query);
    });
  }, [units, query]);
  const visible = expanded || query ? matches : matches.slice(0, 4);

  if (units.length === 0) {
    return (
      <div className="border border-dashed border-slate-200 rounded-md p-3 text-xs text-slate-500 text-center bg-slate-50/50">
        Keine weiteren Einheiten in diesem Objekt verfügbar.
      </div>
    );
  }

  return (
    <div className="space-y-2" data-testid="additional-units-picker">
      <div className="flex items-center gap-2">
        <div className="relative flex-1">
          <MagnifyingGlass size={14} className="absolute left-2.5 top-1/2 -translate-y-1/2 text-slate-400" />
          <Input
            value={q}
            onChange={(e) => setQ(e.target.value)}
            placeholder={`${units.length} Einheiten — Suche nach Nr. / Lage / Typ…`}
            className="pl-8 h-9 text-sm"
            data-testid="additional-units-search"
          />
        </div>
        {!query && units.length > 4 && (
          <button
            type="button"
            onClick={() => setExpanded((v) => !v)}
            className="text-[11px] text-slate-500 hover:text-slate-900 px-2 py-1 rounded border border-slate-200 bg-white shrink-0"
            data-testid="additional-units-toggle-all"
          >
            {expanded ? "Weniger" : `Alle ${units.length} zeigen`}
          </button>
        )}
      </div>

      {visible.length === 0 ? (
        <div className="text-xs text-slate-500 italic p-3 text-center">Keine Treffer.</div>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 gap-1.5">
          {visible.map((u) => {
            const checked = selectedIds.includes(u.id);
            const occupiedByOther = u.active_tenancy && u.active_tenancy.id !== editingTenancyId;
            const showOccupied = occupiedByOther || u.status === "rented";
            return (
              <button
                type="button"
                key={u.id}
                onClick={() => onToggle(u.id)}
                className={`text-left rounded-md border px-2.5 py-2 transition group ${
                  checked
                    ? "bg-indigo-50 border-indigo-300 ring-1 ring-indigo-300"
                    : "bg-white border-slate-200 hover:border-slate-400 hover:bg-slate-50"
                }`}
                data-testid={`add-unit-card-${u.id}`}
              >
                <div className="flex items-start gap-2">
                  <div className={`w-5 h-5 rounded shrink-0 border flex items-center justify-center mt-0.5 ${
                    checked ? "bg-indigo-600 border-indigo-600 text-white" : "border-slate-300 group-hover:border-slate-500"
                  }`}>
                    {checked && <Check size={12} weight="bold" />}
                  </div>
                  <div className="min-w-0 flex-1">
                    <div className="flex items-center gap-1 flex-wrap">
                      <span className="font-mono text-xs font-semibold text-slate-900">{u.unit_number}</span>
                      {showOccupied && (
                        <span className="text-[9px] px-1.5 py-0.5 rounded-full bg-amber-100 text-amber-800 font-medium">belegt</span>
                      )}
                    </div>
                    <div className="text-[11px] text-slate-600 mt-0.5 truncate">
                      {u.location || <span className="text-slate-400 italic">ohne Lage</span>}
                      {u.unit_kind != null && u.unit_kind !== 1 && <span className="text-slate-400"> · Typ {u.unit_kind}</span>}
                    </div>
                  </div>
                </div>
              </button>
            );
          })}
        </div>
      )}
    </div>
  );
}


// 2026-07 — Fläche/Zimmer-Summary bei "unified" Mietverhältnis.
// Ersetzt die frühere PerUnitRentsTable, da bei "einheitlich" die Miete
// NICHT aufsummiert wird (steht als Gesamt-Betrag oben im Hauptformular).
function UnifiedAreaSummary({ mainUnit, additionalUnits }) {
  const all = [mainUnit, ...(additionalUnits || [])].filter(Boolean);
  const total = all.reduce((acc, u) => ({
    living: acc.living + Number(u.living_area || 0),
    usable: acc.usable + Number(u.usable_area || 0),
    rooms:  acc.rooms  + Number(u.rooms || 0),
  }), { living: 0, usable: 0, rooms: 0 });
  const fmtM2 = (n) => n ? `${(Math.round(n * 100) / 100).toLocaleString("de-DE")} m²` : "—";
  const fmtRooms = (n) => n ? (Math.round(n * 10) / 10).toLocaleString("de-DE") : "—";

  return (
    <div className="bg-slate-50 border border-slate-200 rounded-md p-3 space-y-2" data-testid="unified-area-summary">
      <div className="flex items-start justify-between mb-1 gap-3">
        <div>
          <div className="text-xs font-semibold text-slate-800">Aggregierte Werte</div>
          <div className="text-[11px] text-slate-500">
            Wohnfläche, Nutzfläche und Zimmer werden für Dokumente/Makros addiert.
            Die Miete oben gilt für das gesamte Mietverhältnis (einheitlich, keine Aufsummierung).
          </div>
        </div>
      </div>
      <div className="grid grid-cols-12 gap-2 items-center text-[10px] uppercase tracking-wider text-slate-500 font-semibold pb-1 border-b border-slate-200">
        <div className="col-span-5">Einheit</div>
        <div className="col-span-3 text-right">Wohnfläche</div>
        <div className="col-span-2 text-right">Nutzfl.</div>
        <div className="col-span-2 text-right">Zimmer</div>
      </div>
      {all.map((u, i) => (
        <div key={u.id} className="grid grid-cols-12 gap-2 items-center text-xs py-0.5">
          <div className="col-span-5 flex items-center gap-1.5 min-w-0">
            <span className={`px-1 py-0.5 rounded text-[9px] uppercase tracking-wider font-semibold ${i === 0 ? "bg-slate-900 text-white" : "bg-slate-200 text-slate-700"}`}>
              {i === 0 ? "Haupt" : "Zusatz"}
            </span>
            <span className="font-mono text-[11px] truncate text-slate-700" title={u.unit_number}>{u.unit_number}</span>
          </div>
          <div className="col-span-3 text-right tabular-nums text-slate-600">{fmtM2(u.living_area)}</div>
          <div className="col-span-2 text-right tabular-nums text-slate-600">{fmtM2(u.usable_area)}</div>
          <div className="col-span-2 text-right tabular-nums text-slate-600">{fmtRooms(u.rooms)}</div>
        </div>
      ))}
      <div className="grid grid-cols-12 gap-2 items-center text-xs font-semibold pt-1.5 border-t border-slate-300" data-testid="unified-area-total">
        <div className="col-span-5 text-slate-900">∑ Gesamt ({all.length} Einheit{all.length === 1 ? "" : "en"})</div>
        <div className="col-span-3 text-right tabular-nums text-slate-900">{fmtM2(total.living)}</div>
        <div className="col-span-2 text-right tabular-nums text-slate-900">{fmtM2(total.usable)}</div>
        <div className="col-span-2 text-right tabular-nums text-slate-900">{fmtRooms(total.rooms)}</div>
      </div>
    </div>
  );
}


// Phase 21β-1.1: Pro-Einheit-Mietangaben mit Auto-Summen-Button (Legacy — nur noch für alte Daten, wird ab 2026-07 nicht mehr im UI verwendet)
function PerUnitRentsTable({
  mainUnit, additionalUnits, additional_unit_rents,
  mainRentCold, mainUtilities, mainHeating, mainParking,
  onChangeRents, onApplySums,
}) {
  const setRent = (id, key, val) => {
    const next = { ...(additional_unit_rents || {}) };
    const obj = { ...(next[id] || {}) };
    if (val === "" || val == null) delete obj[key]; else obj[key] = Number(val);
    if (Object.keys(obj).length === 0) delete next[id]; else next[id] = obj;
    onChangeRents(next);
  };

  // Summen über alle Zusatz-Einheiten + Haupt-Werte
  const sums = additionalUnits.reduce((acc, u) => {
    const r = (additional_unit_rents || {})[u.id] || {};
    acc.rent_cold += Number(r.rent_cold || 0);
    acc.utilities += Number(r.utilities_advance || 0);
    acc.heating += Number(r.heating_advance || 0);
    acc.parking += Number(r.parking_rent || 0);
    return acc;
  }, { rent_cold: 0, utilities: 0, heating: 0, parking: 0 });

  const totalCold = mainRentCold + sums.rent_cold;
  const totalUtil = mainUtilities + sums.utilities;
  const totalHeat = mainHeating + sums.heating;
  const totalPark = mainParking + sums.parking;

  return (
    <div className="bg-slate-50 border border-slate-200 rounded-md p-3" data-testid="per-unit-rents">
      <div className="flex items-start justify-between mb-2 gap-3">
        <div>
          <div className="text-xs font-semibold text-slate-800">Pro-Einheit-Mietangaben</div>
          <div className="text-[11px] text-slate-500">
            Für jede Einheit einzeln eintragen (optional, leer = trägt 0 € bei). Klicke unten auf „Summen übernehmen", um Miete & NK des Vertrags automatisch zu setzen.
          </div>
        </div>
      </div>

      {/* Header */}
      <div className="grid grid-cols-12 gap-2 items-center text-[10px] uppercase tracking-wider text-slate-500 font-semibold pb-1 border-b border-slate-200 mb-1.5">
        <div className="col-span-4">Einheit</div>
        <div className="col-span-2 text-right">Kalt</div>
        <div className="col-span-2 text-right">NK</div>
        <div className="col-span-2 text-right">Heiz</div>
        <div className="col-span-2 text-right">Stellpl.</div>
      </div>

      {/* Hauptmiete (read-only display) */}
      {mainUnit && (
        <div className="grid grid-cols-12 gap-2 items-center text-xs py-1 border-b border-slate-100">
          <div className="col-span-4 flex items-center gap-1.5 min-w-0">
            <span className="px-1 py-0.5 rounded text-[9px] uppercase tracking-wider font-semibold bg-slate-900 text-white">Haupt</span>
            <span className="font-mono text-[11px] truncate text-slate-700" title={mainUnit.unit_number}>{mainUnit.unit_number}</span>
          </div>
          <div className="col-span-2 text-right tabular-nums text-slate-500 italic text-[11px]">{formatEUR(mainRentCold)}</div>
          <div className="col-span-2 text-right tabular-nums text-slate-500 italic text-[11px]">{formatEUR(mainUtilities)}</div>
          <div className="col-span-2 text-right tabular-nums text-slate-500 italic text-[11px]">{formatEUR(mainHeating)}</div>
          <div className="col-span-2 text-right tabular-nums text-slate-500 italic text-[11px]">{formatEUR(mainParking)}</div>
        </div>
      )}

      {/* Zusatz-Einheiten (editable) */}
      <div className="space-y-1 mt-1">
        {additionalUnits.map((u) => {
          const cur = (additional_unit_rents || {})[u.id] || {};
          return (
            <div key={u.id} className="grid grid-cols-12 gap-2 items-center text-xs">
              <div className="col-span-4 font-mono text-[11px] truncate text-slate-700" title={u.unit_number}>{u.unit_number}</div>
              <Input className="col-span-2 h-7 text-xs text-right tabular-nums" type="number" step="0.01" placeholder="0,00" value={cur.rent_cold ?? ""} onChange={(e) => setRent(u.id, "rent_cold", e.target.value)} data-testid={`per-unit-rent-cold-${u.id}`} />
              <Input className="col-span-2 h-7 text-xs text-right tabular-nums" type="number" step="0.01" placeholder="0,00" value={cur.utilities_advance ?? ""} onChange={(e) => setRent(u.id, "utilities_advance", e.target.value)} />
              <Input className="col-span-2 h-7 text-xs text-right tabular-nums" type="number" step="0.01" placeholder="0,00" value={cur.heating_advance ?? ""} onChange={(e) => setRent(u.id, "heating_advance", e.target.value)} />
              <Input className="col-span-2 h-7 text-xs text-right tabular-nums" type="number" step="0.01" placeholder="0,00" value={cur.parking_rent ?? ""} onChange={(e) => setRent(u.id, "parking_rent", e.target.value)} />
            </div>
          );
        })}
      </div>

      {/* Summenzeile + Auto-Apply */}
      <div className="grid grid-cols-12 gap-2 items-center text-xs pt-2 mt-2 border-t-2 border-slate-300 font-semibold text-slate-900">
        <div className="col-span-4 text-[10px] uppercase tracking-wider text-slate-700">Σ Summe</div>
        <div className="col-span-2 text-right tabular-nums">{formatEUR(totalCold)}</div>
        <div className="col-span-2 text-right tabular-nums">{formatEUR(totalUtil)}</div>
        <div className="col-span-2 text-right tabular-nums">{formatEUR(totalHeat)}</div>
        <div className="col-span-2 text-right tabular-nums">{formatEUR(totalPark)}</div>
      </div>

      <div className="flex justify-end mt-2">
        <Button
          type="button"
          size="sm"
          variant="outline"
          className="h-8 text-xs gap-1.5 bg-amber-600 hover:bg-amber-700 text-white border-amber-600"
          onClick={() => onApplySums({
            rent_cold: totalCold,
            utilities: totalUtil,
            heating: totalHeat,
            parking: totalPark,
          })}
          data-testid="apply-sums-btn"
        >
          ↓ Summen in „Miete & Nebenkosten" übernehmen
        </Button>
      </div>
    </div>
  );
}


// iter168 · Bulk-Versand-Dialog — Amtliches Dokument an mehrere MV senden.
function BulkSendDialog({ open, onClose, tenancyIds, onDone }) {
  const [templates, setTemplates] = useState([]);
  const [templateId, setTemplateId] = useState("");
  const [method, setMethod] = useState("post");
  const [subject, setSubject] = useState("");
  const [busy, setBusy] = useState(false);
  const [result, setResult] = useState(null);

  useEffect(() => {
    if (!open) { setResult(null); return; }
    api.get("/official-documents/templates").then(({ data }) => {
      setTemplates(data.templates || []);
    });
  }, [open]);

  const submit = async () => {
    if (!templateId) { toast.error("Bitte Vorlage wählen"); return; }
    setBusy(true);
    try {
      const { data } = await api.post("/official-documents/bulk-send", {
        template_id: templateId,
        tenancy_ids: tenancyIds,
        method,
        subject: subject || null,
      });
      setResult(data);
      if (data.errors > 0) {
        toast.warning(`${data.ok} versendet · ${data.errors} Fehler`);
      } else {
        toast.success(`${data.ok} Mietverhältnisse erfolgreich verarbeitet`);
      }
      onDone?.();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Bulk-Versand fehlgeschlagen");
    } finally { setBusy(false); }
  };

  if (!open) return null;
  return (
    <Dialog open={open} onOpenChange={(v) => !v && onClose?.()}>
      <DialogContent className="max-w-lg" data-testid="bulk-send-dialog">
        <DialogHeader>
          <DialogTitle>Massenversand — {tenancyIds.length} Mietverhältnisse</DialogTitle>
        </DialogHeader>
        {!result ? (
          <div className="space-y-3">
            <div>
              <Label className="text-xs">Vorlage</Label>
              <select
                value={templateId}
                onChange={(e) => {
                  setTemplateId(e.target.value);
                  const t = templates.find((x) => x.id === e.target.value);
                  setSubject(t?.default_subject || t?.name || "");
                }}
                className="w-full h-9 border rounded px-2 text-sm bg-white"
                data-testid="bulk-send-template"
              >
                <option value="">— bitte wählen —</option>
                {templates.map((t) => (
                  <option key={t.id} value={t.id}>{t.category} · {t.name}</option>
                ))}
              </select>
            </div>
            <div>
              <Label className="text-xs">Betreff</Label>
              <Input value={subject} onChange={(e) => setSubject(e.target.value)}
                     data-testid="bulk-send-subject" />
            </div>
            <div>
              <Label className="text-xs">Versandart</Label>
              <div className="grid grid-cols-3 gap-2 mt-1">
                <label className={`border rounded-md px-2 py-1.5 text-xs cursor-pointer text-center ${method === "post" ? "border-teal-600 bg-teal-50" : "border-slate-200"}`}>
                  <input type="radio" checked={method === "post"} onChange={() => setMethod("post")} className="hidden" data-testid="bulk-send-method-post" />
                  Post (PDF)
                </label>
                <label className={`border rounded-md px-2 py-1.5 text-xs cursor-pointer text-center ${method === "email" ? "border-teal-600 bg-teal-50" : "border-slate-200"}`}>
                  <input type="radio" checked={method === "email"} onChange={() => setMethod("email")} className="hidden" data-testid="bulk-send-method-email" />
                  E-Mail
                </label>
                <label className={`border rounded-md px-2 py-1.5 text-xs cursor-pointer text-center ${method === "download" ? "border-teal-600 bg-teal-50" : "border-slate-200"}`}>
                  <input type="radio" checked={method === "download"} onChange={() => setMethod("download")} className="hidden" data-testid="bulk-send-method-download" />
                  Nur PDF (Mappe)
                </label>
              </div>
            </div>
            <div className="bg-amber-50 border border-amber-200 rounded-md p-2 text-[11px] text-amber-800">
              Der Massenversand erzeugt für jedes Mietverhältnis ein eigenes Dokument in dessen Mappe.
              Max. 200 MV pro Aufruf. Vorschau nicht möglich — bitte an einem einzelnen MV vorher testen.
            </div>
          </div>
        ) : (
          <div className="space-y-2" data-testid="bulk-send-result">
            <div className="text-sm">
              <span className="font-semibold text-teal-700">{result.ok}</span> erfolgreich ·{" "}
              <span className="font-semibold text-rose-700">{result.errors}</span> Fehler
            </div>
            {result.errors > 0 && (
              <ul className="max-h-48 overflow-y-auto text-xs space-y-1 bg-rose-50 border border-rose-200 rounded-md p-2">
                {result.results.filter((r) => !r.ok).map((r, i) => (
                  <li key={i}>
                    <span className="font-mono">{r.tenancy_id.slice(0, 8)}</span>: {r.error}
                  </li>
                ))}
              </ul>
            )}
          </div>
        )}
        <DialogFooter>
          {!result ? (
            <>
              <Button variant="ghost" onClick={onClose}>Abbrechen</Button>
              <Button className="bg-teal-600 hover:bg-teal-700 text-white"
                      onClick={submit} disabled={busy || !templateId}
                      data-testid="bulk-send-submit">
                {busy ? "Läuft…" : `An ${tenancyIds.length} MV senden`}
              </Button>
            </>
          ) : (
            <Button onClick={onClose} data-testid="bulk-send-close">Schließen</Button>
          )}
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

